import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useAuth } from './context/AuthContext';
import api from './utils/api';
import './styles/app.css';

// ============================================
// GLOBAL CONFIGURATION (API & SERVER URL)
// ============================================
export const BASE_SERVER_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace(/\/backend\/api.*$/, '') 
  : 'http://localhost/hannibal-park';

// ============================================
// CONSTANTS
// ============================================
const SITE_IMAGES = {
  logo: '/logo.png',
  mascot: '/mascot.png',
  heroChar: '/hero-character.png',
};

const NAV_LINKS = [
  { path: '/', label: 'Home', labelFr: 'Accueil' },
  { path: '/plan-your-visit', label: 'Plan Your Visit', labelFr: 'Planifier' },
  { path: '/park-rules', label: 'Park Rules', labelFr: 'Règles' },
  { path: '/gallery', label: 'Our Gallery', labelFr: 'Galerie' },
  { path: '/attractions', label: 'Attractions', labelFr: 'Attractions' },
  { path: '/events', label: 'Show & Events', labelFr: 'Événements' },
  { path: '/news', label: 'News', labelFr: 'Actualités' },
  { path: '/contact', label: 'Contact', labelFr: 'Contact' },
];

const TICKET_PLANS = [
  {
    name: 'Basic Pass', price: '20.00', currency: 'DT', points: 20,
    validity: '1 Day', queue: true, games: '3 to 4', popular: false,
    features: ['Get 20 Points', 'Enjoy 1 Day of free access', 'No waiting queue', 'Between 3 to 4 Games']
  },
  {
    name: 'Standard Pass', price: '50.00', currency: 'DT', points: 55,
    validity: '3 Days', queue: true, games: '7 to 9', popular: true,
    features: ['Get 55 Points', 'Enjoy 3 Days of free access', 'No waiting queue', 'Between 7 to 9 Games']
  },
  {
    name: 'Premium Pass', price: '100.00', currency: 'DT', points: 120,
    validity: '10 Days', queue: true, games: '10 to 12', popular: false,
    features: ['Get 120 Points', 'Enjoy 10 Days of free access', 'No waiting queue', 'Between 10 to 12 Games']
  }
];

const REVIEWS = [
  { name: 'Jelena Rakovic', country: 'Russia', flag: '🇷🇺', text: 'Such a happy and joyfully place. Great for entertainment, from kids to adults — there is a lot of different programs. Recommend!', image: 'https://www.hannibalpark.com/assets/img/reviewer-1.png' },
  { name: 'Philipp Fleischer', country: 'Germany', flag: '🇩🇪', text: 'We paid around 100 TND and had so much fun the whole day for 5 persons. Wonderful place with activities for everyone!', image: 'https://www.hannibalpark.com/assets/img/reviewer-2.png' },
  { name: 'Rislaine Mehi', country: 'United Kingdom', flag: '🇬🇧', text: 'Excellent amusement park! The card system for attractions is great — it lets you manage your budget as you wish. Highly recommended.', image: 'https://www.hannibalpark.com/assets/img/reviewer-3.png' },
  { name: 'Desislava Boeva', country: 'USA', flag: '🇺🇸', text: 'Lovely place — feels like in USA! Many shops, cafes and restaurants. Easy to reach from every hotel by taxi. A fantastic family day!', image: 'https://www.hannibalpark.com/assets/img/reviewer-4.png' },
  { name: 'Nathalie Ouilani', country: 'France', flag: '🇫🇷', text: 'Rides for all ages, constant entertainment, sound, lights and tempting smells — a perfect cocktail for an amazing time with the family!', image: 'https://www.hannibalpark.com/assets/img/reviewer-5.png' },
];

// ============================================
// ICON HELPER
// ============================================
const MIcon = ({ name, size, style = {}, className = '' }) => (
  <span
    className={`material-icon ${className}`}
    style={{ fontSize: size || '1.2em', ...style }}
  >
    {name}
  </span>
);

const CATEGORIES = [
  { name: 'Attractions For Adventure', slug: 'attractions-for-adventure', image: 'https://www.hannibalpark.com/assets/img/categories/category_68dd041069458.jpg' },
  { name: 'Attractions For Childs', slug: 'attractions-for-childs', image: 'https://www.hannibalpark.com/assets/img/categories/category_68dd043e76090.jpg' },
  { name: 'Attractions For Parents', slug: 'attractions-for-parents', image: 'https://www.hannibalpark.com/assets/img/categories/category_68dd044b33ba1.jpg' },
];

const GALLERY_IMAGES = [
  'https://www.hannibalpark.com/assets/img/gallery-img-1.jpg',
  'https://www.hannibalpark.com/assets/img/gallery-img-2.jpg',
  'https://www.hannibalpark.com/assets/img/gallery-img-3.jpg',
  'https://www.hannibalpark.com/assets/img/gallery-img-4.jpg',
  'https://www.hannibalpark.com/assets/img/gallery-img-5.jpg',
  'https://www.hannibalpark.com/assets/img/gallery-img-6.jpg',
  'https://www.hannibalpark.com/assets/img/gallery-img-7.jpg',
];

// ============================================
// HOOKS
// ============================================
function useScrollPosition() {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handler = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return scrollY;
}


function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ============================================
// SIMPLE ROUTER
// ============================================
function useRouter() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);
  const navigate = useCallback((to) => {
    window.history.pushState({}, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);
  return { path, navigate };
}

// ============================================
// NAVBAR
// ============================================
function Navbar({ currentPath, navigate, lang, setLang }) {
  const scrollY = useScrollPosition();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <nav className={`navbar ${scrollY > 50 ? 'scrolled' : ''}`}>
      <div className="navbar-inner">
        <a href="/" className="navbar-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <img src={SITE_IMAGES.logo} alt="Hannibal Park" />
        </a>

        <ul className={`navbar-menu ${mobileOpen ? 'open' : ''}`}>
          {NAV_LINKS.map(link => (
            <li key={link.path}>
              <a
                href={link.path}
                className={currentPath === link.path ? 'active' : ''}
                onClick={e => { e.preventDefault(); navigate(link.path); setMobileOpen(false); }}
              >
                {lang === 'fr' ? link.labelFr : link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="navbar-actions">
          <button className="btn-lang" onClick={() => setLang(l => l === 'en' ? 'fr' : 'en')}>
            {lang === 'en' ? 'FR' : 'EN'}
          </button>
          
          {user ? (
            <div className="user-nav">
              <button className="btn-profile" onClick={() => navigate('/profile')}>
                {lang === 'fr' ? 'Salut' : 'Hi'}, {user.first_name}
              </button>
              <button className="btn-logout" onClick={logout} title={lang === 'fr' ? 'Se déconnecter' : 'Logout'}>
                {lang === 'fr' ? 'Déconnexion' : 'Logout'}
              </button>
            </div>
          ) : (
            <a href="/login" className="btn-login" onClick={e => { e.preventDefault(); navigate('/login'); }} title={lang === 'fr' ? 'Se connecter' : 'Log In'} aria-label="Log In">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </a>
          )}

          <a href="/buy-tickets" className="btn-tickets" onClick={e => { e.preventDefault(); navigate('/buy-tickets'); }}>
            {lang === 'fr' ? 'Acheter Tickets' : 'Buy Tickets'}
          </a>
          
          <button className="mobile-toggle" onClick={() => setMobileOpen(o => !o)} aria-label="Toggle menu">
            <span style={mobileOpen ? { transform: 'rotate(45deg) translate(5px, 5px)' } : {}} />
            <span style={mobileOpen ? { opacity: 0 } : {}} />
            <span style={mobileOpen ? { transform: 'rotate(-45deg) translate(5px, -5px)' } : {}} />
          </button>
        </div>
      </div>
    </nav>
  );
}

// ============================================
// HERO SECTION
// ============================================
function HeroSection({ navigate, lang, settings }) {
  const { user } = useAuth();
  const getOpeningHours = () => {
    if (settings && settings.hours) {
      return lang === 'fr' 
        ? (settings.hours.opening_hours_fr || settings.hours.opening_hours_en)
        : settings.hours.opening_hours_en;
    }
    return lang === 'fr' ? 'Ouvert: 14h à 00h' : '4PM – 12AM';
  };

  return (
    <section className="hero" id="top" aria-label="Welcome to Hannibal Park" style={{ background: "url('/hero-bg.jpg') center/cover no-repeat" }}>
      <div className="hero-topbar" aria-hidden="true"></div>
      <div className="bg-blob blob-teal" aria-hidden="true"></div>
      <div className="bg-blob blob-gold" aria-hidden="true"></div>
      <div className="hero-panel" aria-hidden="true"></div>
      <div className="hero-dots" aria-hidden="true"></div>

      <i className="dstar ds1" aria-hidden="true">✦</i>
      <i className="dstar ds2" aria-hidden="true">✦</i>
      <i className="dstar ds3" aria-hidden="true">✦</i>
      <i className="dstar ds4" aria-hidden="true">✦</i>

      <div className="hero-inner">
        {/* LEFT: Content */}
        <div className="hero-content-new" id="hero-left">
          <div className="hero-badge">{lang === 'fr' ? 'Un Paradis pour les Enfants & Une Joie pour les Parents' : 'A Paradise for Children & A Joy for Parents'}</div>

          <h1 className="hero-h1" id="h1">
            Hannibal Park
            <span className="line-teal">Sousse</span>
            <img src="/hannibal-logo-text.png" alt="Hannibal Park" className="hero-logo-text" />
          </h1>


          <div className="hero-actions">
            {user ? (
              <a href="/profile" className="btn-main" onClick={e => { e.preventDefault(); navigate('/profile'); }}>
                <MIcon name="person" /> {lang === 'fr' ? 'Mon Profil' : 'My Profile'}
              </a>
            ) : (
              <a href="/register" className="btn-main" onClick={e => { e.preventDefault(); navigate('/register'); }}>
                <MIcon name="assignment" /> {lang === 'fr' ? 'Inscrivez-vous' : 'Register Now'}
              </a>
            )}
            <a href="/buy-tickets" className="btn-sec" onClick={e => { e.preventDefault(); navigate('/buy-tickets'); }}>
              <MIcon name="local_activity" /> {lang === 'fr' ? 'Achetez vos Tickets' : 'Buy Your Tickets'}
            </a>
          </div>

          <div className="hero-stats">
            <div className="h-stat">
              <div className="h-stat-num">32+</div>
              <div className="h-stat-lbl">{lang === 'fr' ? 'Manèges & Attractions' : 'Rides & Attractions'}</div>
            </div>
            <div className="h-stat">
              <div className="h-stat-num">4+</div>
              <div className="h-stat-lbl">Restaurants</div>
            </div>
            <div className="h-stat">
              <div className="h-stat-num">4.8★</div>
              <div className="h-stat-lbl">{lang === 'fr' ? 'Avis Clients' : 'Guest Rating'}</div>
            </div>
          </div>


        </div>

        {/* RIGHT: Mascot */}
        <div className="hero-right" id="hero-right" aria-hidden="true">
          <div className="mascot-glow"></div>
          <div className="mascot-shadow"></div>

          <div className="mbadge mb-rides">
            <span className="mb-icon"><MIcon name="attractions" size="1.8rem" /></span>
            <div><div className="mb-val">32+</div><div className="mb-lbl">{lang === 'fr' ? 'Manèges' : 'Rides'}</div></div>
          </div>
          <div className="mbadge mb-rating">
            <span className="mb-icon"><MIcon name="star" size="1.8rem" /></span>
            <div><div className="mb-val">4.8</div><div className="mb-lbl">{lang === 'fr' ? 'Avis' : 'Rating'}</div></div>
          </div>


          <img src={SITE_IMAGES.heroChar}
               className="mascot-img"
               alt="Hannibal Park mascot"
               width="560" height="560" />
        </div>
      </div>

      <div className="hero-hours" role="complementary" aria-label="Opening hours">
        <div className="hero-hours-label">{lang === 'fr' ? "Heures d'ouverture" : "Opening Hours"}</div>
        <div className="hero-hours-time">{getOpeningHours()}</div>
        <div className="hero-hours-days">{lang === 'fr' ? "Lundi au Dimanche" : "Monday to Sunday"}</div>
        <div className="open-dot">{lang === 'fr' ? "Ouvert Aujourd'hui" : "Open Today"}</div>
      </div>
    </section>
  );
}

// ============================================
// ANIMATED SECTION WRAPPER
// ============================================
function AnimatedSection({ children, className = '', ...props }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={`fade-in ${visible ? 'visible' : ''} ${className}`} {...props}>
      {children}
    </div>
  );
}

// ============================================
// ABOUT SECTION
// ============================================
function AboutSection({ navigate, lang, settings }) {
  const getOpeningHours = () => {
    if (settings && settings.hours) {
      return lang === 'fr' 
        ? (settings.hours.opening_hours_fr || settings.hours.opening_hours_en)
        : settings.hours.opening_hours_en;
    }
    return lang === 'fr' ? 'Ouvert: 14h à 00h' : 'Open: 2PM to 12AM';
  };

  return (
    <section className="section">
      <div className="container">
        <AnimatedSection>
          <div className="about-grid">
            <div className="about-image">
              <img src={SITE_IMAGES.mascot} alt="Hannibal Park Mascot" style={{ borderRadius: '24px' }} />
              <div className="about-image-badge">
                <span className="number">32+</span>
                <span className="label">{lang === 'fr' ? 'Attractions Palpitantes' : 'Exciting Rides'}</span>
              </div>
            </div>
            <div className="about-text">
              <div className="section-badge">{lang === 'fr' ? 'À propos du parc' : 'About the park'}</div>
              <h2>{lang === 'fr' ? 'Profitez d\'un monde plus merveilleux à Hannibal Park' : 'Enjoy a more wonderful world at Hannibal Park'}</h2>
              <p>
                {lang === 'fr' 
                  ? 'Hannibal Park, situé au cœur de la zone touristique de Kantaoui à Sousse, est un parc d\'attractions vibrant parfait pour les familles, les jeunes et les enfants. Avec plus de 32 manèges et attractions passionnants, il y en a pour tous les goûts, des montagnes russes palpitantes aux manèges doux pour les tout-petits. Le parc offre une sélection de plus de 3 cafés et 4 restaurants.'
                  : 'Hannibal Park, located in the heart of the Kantaoui tourist zone in Sousse, is a vibrant amusement park perfect for families, young people, and children. With more than 32 exciting rides and attractions, there\'s something for everyone, from thrilling roller coasters to gentle rides for the little ones. The park offers a selection of over 3 cafes and 4 restaurants, providing a variety of dining options to satisfy every taste.'}
              </p>
              <div className="hours-card">
                <span className="icon"><MIcon name="schedule" size="1.8rem" /></span>
                <div>
                  <div className="text">{lang === 'fr' ? "Heures d'ouverture" : "Opening Hours"}</div>
                  <div className="time">{getOpeningHours()}</div>
                </div>
              </div>
              <br />
              <a href="/plan-your-visit" className="btn-main" onClick={e => { e.preventDefault(); navigate('/plan-your-visit'); }}>
                {lang === 'fr' ? 'Planifiez votre visite' : 'Plan Your Visit'} <span>→</span>
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================
// TICKETS SECTION
// ============================================
function TicketsSection({ navigate, lang }) {
  const [plans, setPlans] = useState([]);
  const [totalTickets, setTotalTickets] = useState(999);
  const [totalPlans, setTotalPlans] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getTicketPlans()
      .then(data => {
        setPlans(data.plans || []);
        if (data.total_tickets !== undefined) setTotalTickets(data.total_tickets);
        if (data.total_plans !== undefined) setTotalPlans(data.total_plans);
      })
      .catch(err => {
        console.error("Failed to load ticket plans:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const getParsedFeatures = (plan) => {
    let parsedFeatures = [];
    try {
      parsedFeatures = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
    } catch (e) {
      parsedFeatures = [];
    }
    if (parsedFeatures.length === 0) {
      parsedFeatures.push(lang === 'fr' ? `Obtenez ${plan.points} Points` : `Get ${plan.points} Points`);
      parsedFeatures.push(lang === 'fr' ? `Accès libre pendant ${plan.validity_days} jour(s)` : `Enjoy ${plan.validity_days} Day(s) of free access`);
      if (plan.skip_queue) {
        parsedFeatures.push(lang === 'fr' ? 'Accès coupe-file inclus' : 'No waiting queue (Skip Queue)');
      }
      if (plan.games_range) {
        parsedFeatures.push(lang === 'fr' ? `Gamme de jeux : ${plan.games_range}` : `Games range: ${plan.games_range}`);
      }
    }
    return parsedFeatures;
  };

  const fallbackPlans = TICKET_PLANS.map(p => ({
    name_en: p.name,
    name_fr: p.name,
    price: p.price,
    currency: p.currency,
    is_popular: p.popular,
    features: p.features
  }));

  const listToRender = plans.length > 0 ? plans : fallbackPlans;

  return (
    <section className="section section-light">
      <div className="container">
        <AnimatedSection>
          <div className="section-header">
            <div className="section-badge"><MIcon name="local_activity" /> {lang === 'fr' ? 'Tarifs' : 'Pricing'}</div>
            <h2 className="section-title">
              {lang === 'fr' ? 'Choisissez vos tickets' : 'Choose Your Tickets'}
            </h2>
            <p className="section-subtitle">
              {lang === 'fr' 
                ? 'Sélectionnez le pass parfait pour votre aventure à Hannibal Park' 
                : 'Select the perfect pass for your adventure at Hannibal Park'}
            </p>
          </div>
        </AnimatedSection>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', minHeight: '300px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="cat-skeleton" style={{ height: '380px', borderRadius: '24px' }} />
            ))}
          </div>
        ) : (
          <div className="tickets-grid">
            {listToRender.map((plan, i) => {
              const name = lang === 'fr' ? (plan.name_fr || plan.name_en) : plan.name_en;
              const features = getParsedFeatures(plan);
              const isPopular = plan.is_popular;
              const isOutOfStock = totalTickets < totalPlans;
              return (
                <AnimatedSection key={plan.id || i}>
                  <div className={`ticket-card ${isPopular ? 'popular' : ''}`}>
                    {!!isPopular && <div className="ticket-popular-badge">{lang === 'fr' ? 'Le Plus Populaire' : 'Most Popular'}</div>}
                    <div className="ticket-name">{name}</div>
                    <div className="ticket-price">
                      {plan.price}<span>{plan.currency || 'TND'}</span>
                    </div>

                    <div className={`ticket-stock ${isOutOfStock ? 'out-of-stock' : 'in-stock'}`} style={{ textAlign: 'center', margin: '10px 0', fontWeight: 'bold', color: isOutOfStock ? '#dc2626' : '#16a34a' }}>
                      {isOutOfStock 
                        ? (lang === 'fr' ? 'Rupture de Stock' : 'Out Of Stock') 
                        : (lang === 'fr' ? 'Stock Disponible' : 'Available Stock')}
                    </div>

                    <ul className="ticket-features">
                      {features.map((f, j) => (
                        <li key={j}><span className="check">✓</span> {f}</li>
                      ))}
                    </ul>
                    {isOutOfStock ? (
                      <span className="ticket-btn" style={{ background: '#9ca3af', cursor: 'not-allowed', display: 'inline-block', textAlign: 'center' }}>
                        {lang === 'fr' ? 'Rupture de Stock' : 'Out Of Stock'}
                      </span>
                    ) : (
                      <a href="/buy-tickets" className="ticket-btn" onClick={e => { e.preventDefault(); navigate('/buy-tickets'); }} style={{ background: isPopular ? 'var(--hp-gradient-cta)' : 'var(--hp-gradient-blue)' }}>
                        {lang === 'fr' ? 'Acheter vos Tickets' : 'Buy Your Tickets'}
                      </a>
                    )}
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================
// CATEGORIES SECTION
// ============================================
function CategoriesSection({ navigate, lang }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCategories()
      .then(data => {
        setCategories(data.categories || []);
      })
      .catch(err => {
        console.error("Failed to load categories:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const getCategoryImageUrl = (cat) => {
    if (!cat.image) return 'https://www.hannibalpark.com/assets/img/categories/category_68dd041069458.jpg';
    if (cat.image.startsWith('http://') || cat.image.startsWith('https://')) {
      return cat.image;
    }
    const imageName = cat.image.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/categories/${imageName}`;
  };

  const listToRender = categories.length > 0 ? categories : CATEGORIES;

  return (
    <section className="section">
      <div className="container">
        <AnimatedSection>
          <div className="section-header">
            <div className="section-badge"><MIcon name="attractions" /> {lang === 'fr' ? 'Parc d\'attractions' : 'Amusement Park'}</div>
            <h2 className="section-title">
              {lang === 'fr' ? 'Découvrez nos jeux les plus populaires' : 'Discover our Most Popular Games'}
            </h2>
          </div>
        </AnimatedSection>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', minHeight: '300px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="cat-skeleton" style={{ height: '320px', borderRadius: '24px' }} />
            ))}
          </div>
        ) : (
          <div className="categories-grid">
            {listToRender.map((cat, i) => {
              const name = lang === 'fr' ? (cat.name_fr || cat.name_en) : cat.name_en;
              return (
                <AnimatedSection key={cat.id || i}>
                  <div className="category-card" onClick={() => navigate(`/category/${cat.slug}`)}>
                    <img src={getCategoryImageUrl(cat)} alt={name} />
                    <div className="category-overlay">
                      <h3>{name}</h3>
                      <span className="link">{lang === 'fr' ? 'Explorer →' : 'Explore →'}</span>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================
// LATEST EVENTS SECTION
// ============================================
function LatestEventsSection({ navigate, lang }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.getEvents()
      .then(data => {
        const sortedEvents = (data.events || []).sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
        setEvents(sortedEvents.slice(0, 3));
      })
      .catch(err => {
        console.error("Failed to load events:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || events.length === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatTime = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':');
    return `${h}h${m}`;
  };

  const getImageUrl = (ev) => {
    if (!ev.image) return null;
    if (ev.image_url && ev.image_url.startsWith('http')) {
      return ev.image_url;
    }
    const imageName = ev.image.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/events/${imageName}`;
  };

  return (
    <section className="section section-light">
      <div className="container">
        <AnimatedSection>
          <div className="section-header">
            <div className="section-badge"><MIcon name="celebration" /> {lang === 'fr' ? 'Derniers Événements' : 'Latest Events'}</div>
            <h2 className="section-title">
              {lang === 'fr' ? 'Découvrez nos derniers événements' : 'Discover Our Latest Events'}
            </h2>
          </div>
        </AnimatedSection>

        <div className="ev-grid">
          {events.map((ev, i) => {
            const title  = lang === 'fr' ? (ev.title_fr || ev.title_en) : ev.title_en;
            const desc   = lang === 'fr' ? (ev.description_fr || ev.description_en) : ev.description_en;
            const imgUrl = getImageUrl(ev);
            const isEnded = ev.event_date < today;
            return (
              <AnimatedSection key={ev.id || i}>
                <article className="ev-card">
                  <div className={`ev-img ${!imgUrl ? 'ev-img-placeholder' : ''}`}>
                    {imgUrl ? <img src={imgUrl} alt={title} loading="lazy" /> : <span><MIcon name="celebration" size="4rem" /></span>}
                    {ev.is_featured == 1 && (
                      <div className="ev-featured"><MIcon name="star" /> {lang === 'fr' ? 'Vedette' : 'Featured'}</div>
                    )}
                    {isEnded && (
                      <div className="ev-ended-label"><MIcon name="block" /> {lang === 'fr' ? 'Terminé' : 'Ended'}</div>
                    )}
                  </div>
                  <div className="ev-body">
                    <div className="ev-meta">
                      <span className="ev-date-badge"><MIcon name="calendar_today" /> {formatDate(ev.event_date)}</span>
                      {ev.event_time && <span className="ev-time-badge"><MIcon name="schedule" /> {formatTime(ev.event_time)}</span>}
                      {isEnded && (
                        <span className="ev-ended-badge"><MIcon name="radio_button_checked" /> {lang === 'fr' ? 'Événement Terminé' : 'Event Ended'}</span>
                      )}
                    </div>
                    <h3 className="ev-title">{title}</h3>
                    {desc && <p className="ev-desc">{desc}</p>}
                    {ev.location && <div className="ev-loc"><MIcon name="location_on" /> {ev.location}</div>}
                  </div>
                </article>
              </AnimatedSection>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <a href="/events" className="btn-main" onClick={e => { e.preventDefault(); navigate('/events'); }}>
            {lang === 'fr' ? 'Voir tous les événements →' : 'View all events →'}
          </a>
        </div>
      </div>
      <style>{`
        .ev-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:28px; margin-top:40px; }
        .ev-card { background:white; border-radius:20px; overflow:hidden; box-shadow:0 4px 24px rgba(0,0,0,.08); transition:transform .3s,box-shadow .3s; }
        .ev-card:hover { transform:translateY(-6px); box-shadow:0 12px 40px rgba(0,0,0,.14); }
        .ev-img { position:relative; height:200px; overflow:hidden; }
        .ev-img img { width:100%; height:100%; object-fit:cover; transition:transform .4s; }
        .ev-card:hover .ev-img img { transform:scale(1.05); }
        .ev-img-placeholder { background:linear-gradient(135deg,#1a1a2e,#2bb5e0); display:flex; align-items:center; justify-content:center; font-size:4rem; }
        .ev-featured { position:absolute; top:12px; left:12px; background:linear-gradient(135deg,#f59e0b,#ef4444); color:white; font-size:11px; font-weight:700; padding:4px 10px; border-radius:20px; }
        .ev-ended-label { position:absolute; top:12px; right:12px; background:rgba(220,38,38,0.9); backdrop-filter:blur(4px); color:white; font-size:11px; font-weight:700; padding:4px 10px; border-radius:20px; box-shadow:0 2px 8px rgba(0,0,0,0.15); z-index:5; }
        .ev-body { padding:20px 22px; }
        .ev-meta { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
        .ev-date-badge { display:inline-flex; align-items:center; gap:5px; background:#eff6ff; color:#1d4ed8; font-size:12px; font-weight:600; padding:4px 10px; border-radius:6px; }
        .ev-time-badge { display:inline-flex; align-items:center; gap:5px; background:#f0fdf4; color:#166534; font-size:12px; font-weight:600; padding:4px 10px; border-radius:6px; }
        .ev-ended-badge { display:inline-flex; align-items:center; gap:5px; background:#fef2f2; color:#dc2626; font-size:12px; font-weight:700; padding:4px 10px; border-radius:6px; border:1px solid #fee2e2; }
        .ev-title { font-size:1.1rem; font-weight:700; margin:0 0 8px; color:#111827; line-height:1.4; }
        .ev-desc { font-size:.88rem; color:#6b7280; line-height:1.6; margin:0 0 10px; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
        .ev-loc { font-size:12px; color:#6b7280; font-weight:600; }
        @media(max-width:640px) { .ev-grid { grid-template-columns:1fr; } }
      `}</style>
    </section>
  );
}

// ============================================
// FEATURES SECTION
// ============================================
function FeaturesSection({ lang }) {
  const features = lang === 'fr' ? [
    { img: 'https://www.hannibalpark.com/assets/img/feature-1.png', title: 'Réservations Flexibles', desc: 'Vous changez d\'avis ? Modifiez ou annulez votre réservation gratuitement jusqu\'à 7 jours avant votre arrivée.' },
    { img: 'https://www.hannibalpark.com/assets/img/feature-2.png', title: 'Paiement en Ligne', desc: 'Achetez vos billets avec votre carte bancaire en ligne sans longues files d\'attente à l\'entrée du parc.' },
    { img: 'https://www.hannibalpark.com/assets/img/feature-3.png', title: 'Experts à Votre Service', desc: 'Planifiez votre visite la plus inoubliable avec l\'aide de nos experts Hannibal Park.' },
    { img: 'https://www.hannibalpark.com/assets/img/feature-4.png', title: 'Parking 24h/24', desc: 'Vastes zones de stationnement autour du parc pouvant accueillir plus de 1 000 véhicules à tout moment.' },
  ] : [
    { img: 'https://www.hannibalpark.com/assets/img/feature-1.png', title: 'Flexible Bookings', desc: 'Change your mind? Modify or cancel your booking for free up to 7 days before arrival.' },
    { img: 'https://www.hannibalpark.com/assets/img/feature-2.png', title: 'Pay Online', desc: 'Buy your tickets with your credit card online without long waiting queues at the park.' },
    { img: 'https://www.hannibalpark.com/assets/img/feature-3.png', title: 'Experts By Your Side', desc: 'Plan your most unforgettable visit with the help of our Hannibal Park Experts.' },
    { img: 'https://www.hannibalpark.com/assets/img/feature-4.png', title: 'Parking 24/7', desc: 'Comfortable parking areas around us to support over 1,000 vehicles at any time.' },
  ];

  return (
    <section className="features" aria-labelledby="feat-h">
      <AnimatedSection className="feat-inner">
        <div className="feat-hd">
          <div className="eyebrow ey-w" style={{ justifyContent: 'center' }}><i></i>{lang === 'fr' ? 'Nos Promesses' : 'Our Promises'}</div>
          <h2 className="stitle" id="feat-h" style={{ color: 'white' }}>{lang === 'fr' ? 'Garantie de' : 'Peace of Mind'} <span style={{ color: 'var(--gold)' }}>{lang === 'fr' ? 'Tranquillité' : 'Guarantee'}</span></h2>
          <p>{lang === 'fr' ? 'Réservez votre visite en toute confiance — nous promettons une expérience inoubliable.' : 'Book your visit with full confidence — we promise an unforgettable experience.'}</p>
        </div>
        <div className="feat-grid">
          {features.map((f, i) => (
            <div className="feat-card" key={i}>
              <div className="feat-ico">
                <img src={f.img} alt="" width="40" height="40" loading="lazy" />
              </div>
              <h3 className="feat-name">{f.title}</h3>
              <p className="feat-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
}

// ============================================
// REVIEWS SECTION
// ============================================
function ReviewsSection({ lang }) {
  return (
    <section className="testi" aria-labelledby="test-h">
      <div className="testi-inner">
        <div className="testi-hd">
          <div className="eyebrow ey-t" style={{ justifyContent: 'center' }}><i></i>{lang === 'fr' ? 'Avis des Visiteurs' : 'Visitors Reviews'}</div>
          <h2 className="stitle" id="test-h">{lang === 'fr' ? 'Ce que nos visiteurs' : 'What Our Guests'} <span className="t">{lang === 'fr' ? 'Disent' : 'Are Saying'}</span></h2>
        </div>
        <div className="tgrid">
          {REVIEWS.map((review, i) => (
            <article className="tc2" key={i}>
              <span className="tc2-q" aria-hidden="true">&ldquo;</span>
              <div className="tc2-stars" aria-label="5 stars">★★★★★</div>
              <p className="tc2-text">{review.text}</p>
              <div className="tc2-auth">
                <img src={review.image} className="tc2-av" alt={review.name} width="46" height="46" loading="lazy" />
                <div>
                  <div className="tc2-name">{review.name}</div>
                  <div className="tc2-from">{review.flag} {lang === 'fr' ? 'De' : 'From'} {review.country}</div>
                </div>
              </div>
            </article>
          ))}
          {/* Summary rating card */}
          <div className="tc2 rating-card">
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '80px', color: 'white', lineHeight: 1 }}>4.8</div>
            <div style={{ color: 'var(--gold)', fontSize: '20px', letterSpacing: '4px' }}>★★★★★</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,.72)', fontWeight: 700 }}>{lang === 'fr' ? 'Basé sur 800+ avis' : 'Based on 800+ reviews'}</div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.45)' }}>Google · TripAdvisor</div>
            <a href="/contact" style={{ marginTop: '8px', background: 'white', color: 'var(--teal)', fontWeight: 800, fontSize: '14px', padding: '10px 22px', borderRadius: '50px', display: 'inline-block' }}>{lang === 'fr' ? 'Laisser un Avis' : 'Write a Review'}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// GALLERY SECTION
// ============================================
function GallerySection({ navigate, lang }) {
  return (
    <section className="section section-light">
      <div className="container">
        <AnimatedSection>
          <div className="section-header">
            <div className="section-badge"><MIcon name="photo_camera" /> {lang === 'fr' ? 'Galerie' : 'Gallery'}</div>
            <h2 className="section-title">{lang === 'fr' ? 'Notre Galerie du Parc' : 'Our Park Gallery'}</h2>
          </div>

          <div className="gallery-grid">
            {GALLERY_IMAGES.slice(0, 5).map((img, i) => (
              <div className="gallery-item" key={i}>
                <img src={img} alt={`Gallery ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <a href="/gallery" className="btn-main" onClick={e => { e.preventDefault(); navigate('/gallery'); }}>
              {lang === 'fr' ? 'Voir toute la Galerie →' : 'View All Gallery →'}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================
// MAP SECTION
// ============================================
function MapSection({ lang, settings }) {
  const getOpeningHours = () => {
    if (settings && settings.hours) {
      return lang === 'fr' 
        ? (settings.hours.opening_hours_fr || settings.hours.opening_hours_en)
        : settings.hours.opening_hours_en;
    }
    return lang === 'fr' ? 'Lundi au Dimanche: 14h à 00h' : 'Monday to Sunday: 4PM – 12AM';
  };

  return (
    <section className="contact" id="contact" aria-labelledby="cont-h">
      <AnimatedSection className="contact-inner">
        <div className="fl">
          <div className="eyebrow ey-t"><i></i>{lang === 'fr' ? 'Notre Emplacement' : 'Our Location'}</div>
          <h2 className="stitle" id="cont-h">{lang === 'fr' ? 'Trouver le' : 'Get Direction'}<br/>{lang === 'fr' ? 'Chemin vers le' : 'To The'} <span className="t">{lang === 'fr' ? 'Parc' : 'Park'}</span></h2>
          <div className="c-detail">
            <div className="c-ico"><MIcon name="location_on" size="1.5rem" /></div>
            <div><div className="c-lbl">{lang === 'fr' ? 'Adresse du Parc' : 'Park Address'}</div><div className="c-val">Kantaoui, Sousse, Tunisia</div></div>
          </div>
          <div className="c-detail">
            <div className="c-ico"><MIcon name="email" size="1.5rem" /></div>
            <div><div className="c-lbl">{lang === 'fr' ? 'Écrivez-nous' : 'Email Us'}</div><div className="c-val"><a href="mailto:contact@hannibalpark.com">contact@hannibalpark.com</a></div></div>
          </div>
          <div className="c-detail">
            <div className="c-ico"><MIcon name="phone" size="1.5rem" /></div>
            <div><div className="c-lbl">{lang === 'fr' ? 'Appelez-nous' : 'Call Us'}</div><div className="c-val"><a href="tel:+21626562000" style={{ color: 'inherit' }}>+216 26 562 000</a></div></div>
          </div>
          <div className="c-detail">
            <div className="c-ico" style={{ background: 'var(--car-l)' }}><MIcon name="schedule" size="1.5rem" /></div>
            <div><div className="c-lbl">{lang === 'fr' ? "Heures d'ouverture" : "Opening Hours"}</div><div className="c-val">{getOpeningHours()}</div></div>
          </div>
          <a href="https://www.google.com/maps/place/Hannibal+Park/@35.8909279,10.5950348" className="btn-main" style={{ marginTop: '24px', display: 'inline-flex' }} target="_blank" rel="noopener noreferrer"><MIcon name="map" /> {lang === 'fr' ? 'Ouvrir dans Google Maps' : 'Open in Google Maps'}</a>
        </div>
        <div className="map-embed fr">
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3186.9!2d10.5950348!3d35.8909279!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fd898d7d16fe65%3A0xc87c1532803ea9d9!2sHannibal+Park!5e0!3m2!1sfr!2stn!4v1" title="Hannibal Park location map" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen></iframe>
        </div>
      </AnimatedSection>
    </section>
  );
}

// ============================================
// NEWSLETTER SECTION
// ============================================
function NewsletterSection({ lang }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [nlLoading, setNlLoading] = useState(false);
  const [nlError, setNlError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNlLoading(true);
    setNlError(null);
    try {
      await api.subscribe(email);
      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setNlError(lang === 'fr' ? 'Erreur, veuillez réessayer.' : 'Error, please try again.');
    } finally {
      setNlLoading(false);
    }
  };

  return (
    <section className="newsletter-bar">
      <div className="newsletter-inner">
        <div>
          <h3>{lang === 'fr' ? 'Rejoignez notre newsletter' : 'Join Our Newsletter'}</h3>
          <p>{lang === 'fr' ? 'Restez informé des dernières actualités, événements et offres exclusives' : 'Stay updated with the latest news, events and exclusive offers'}</p>
        </div>
        <form className="newsletter-form" onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder={lang === 'fr' ? 'Entrez votre e-mail...' : 'Enter your email...'}
            value={email}
            onChange={e => { setEmail(e.target.value); setNlError(null); }}
            required
            disabled={nlLoading || submitted}
          />
          <button type="submit" disabled={nlLoading || submitted}>
            {submitted
              ? (lang === 'fr' ? '✓ Abonné !' : '✓ Subscribed!')
              : nlLoading
                ? (lang === 'fr' ? '...' : '...')
                : (lang === 'fr' ? "S'abonner" : 'Subscribe')}
          </button>
        </form>
        {nlError && <p style={{ color: '#fca5a5', fontSize: '13px', marginTop: '8px' }}>{nlError}</p>}
      </div>
    </section>
  );
}

// ============================================
// NEWS SECTION (Home)
// ============================================
function NewsSection({ navigate, lang }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getNews()
      .then(data => {
        setNews((data.news || []).slice(0, 3));
      })
      .catch(err => console.error("Failed to load news:", err))
      .finally(() => setLoading(false));
  }, []);

  const getImageUrl = (n) => {
    if (!n.image) return 'https://www.hannibalpark.com/assets/img/news/recent-winter-holiday.jpg';
    if (n.image.startsWith('http')) return n.image;
    const imageName = n.image.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/news/${imageName}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (!loading && news.length === 0) return null;

  return (
    <section className="news" aria-labelledby="news-h">
      <div className="news-inner">
        <div className="news-hd">
          <div className="eyebrow ey-c" style={{ justifyContent: 'center' }}><i></i>{lang === 'fr' ? 'Actualités et articles' : 'News & Articles'}</div>
          <h2 className="stitle" id="news-h">{lang === 'fr' ? 'Nos dernières' : 'Our Latest'} <span className="c">{lang === 'fr' ? 'Actualités' : 'News'}</span></h2>
        </div>
        <div className="news-grid">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', width: '100%', color: 'var(--hp-gray-500)' }}>Loading news...</div>
          ) : news.map((n, i) => {
            const title = lang === 'fr' ? (n.title_fr || n.title_en) : n.title_en;
            const exc = lang === 'fr' ? (n.excerpt_fr || n.excerpt_en) : n.excerpt_en;
            return (
              <article className="nc" key={n.id || i}>
                <div className="nc-img"><img src={getImageUrl(n)} alt={title} loading="lazy" width="400" height="225" /></div>
                <div className="nc-body">
                  <div className="nc-date">{formatDate(n.published_at)}</div>
                  <h3 className="nc-title">{title}</h3>
                  <p className="nc-exc">{exc}</p>
                  <a href={`/news/${n.slug}`} className="nc-read" onClick={e => { e.preventDefault(); navigate(`/news/${n.slug}`); }}>{lang === 'fr' ? 'Lire la suite →' : 'Read More →'}</a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================
// FOOTER
// ============================================
function Footer({ navigate, settings, lang }) {
  const { user } = useAuth();
  const fb = settings?.social?.facebook_url || "https://www.facebook.com/hannibalpark.com.tn/";
  const ig = settings?.social?.instagram_url || "https://www.instagram.com/hannibalpark_sousse/";
  const yt = settings?.youtube_url || "https://www.youtube.com/@hannibalpark2548";
  const tt = settings?.tiktok_url || "https://www.tiktok.com/@hannibal_park_sousse__";

  const quickLinks = [
    { path: '/plan-your-visit', label: 'Plan Your Visit', labelFr: 'Planifier votre visite' },
    { path: '/park-rules', label: 'Park Rules', labelFr: 'Règles du parc' },
    { path: '/gallery', label: 'Our Gallery', labelFr: 'Notre Galerie' },
    { path: '/attractions', label: 'Attractions', labelFr: 'Attractions' },
    { path: '/contact', label: 'Contact', labelFr: 'Contact' },
  ];

  const usefulLinks = user
    ? [
        { label: 'My Profile', labelFr: 'Mon Profil', path: '/profile' },
        { label: 'Business & Professionals', labelFr: 'Professionnels', path: '/pro-business' },
      ]
    : [
        { label: 'Log In', labelFr: 'Se connecter', path: '/login' },
        { label: 'Register', labelFr: "S'inscrire", path: '/register' },
        { label: 'Business & Professionals', labelFr: 'Professionnels', path: '/pro-business' },
      ];

  return (
    <>
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <img src={SITE_IMAGES.logo} alt="Hannibal Park" />
              <p>
                {lang === 'fr' 
                  ? 'Le plus grand parc d\'attractions du Sahel situé à Kantaoui Sousse' 
                  : 'The largest amusement park in the Sahel located in Kantaoui Sousse'}
              </p>
              <div className="footer-social">
                {fb && (
                  <a href={fb} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z"/></svg>
                  </a>
                )}
                {ig && (
                  <a href={ig} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                )}
                {yt && (
                  <a href={yt} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                )}
                {tt && (
                  <a href={tt} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  </a>
                )}
              </div>
            </div>

            <div className="footer-col">
              <h4>{lang === 'fr' ? 'Accès Rapide' : 'Quick Access'}</h4>
              <ul className="footer-links">
                {quickLinks.map(link => (
                  <li key={link.path}>
                    <a href={link.path} onClick={e => { e.preventDefault(); navigate(link.path); }}>
                      {lang === 'fr' ? link.labelFr : link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer-col">
              <h4>{lang === 'fr' ? 'Liens Utiles' : 'Useful Links'}</h4>
              <ul className="footer-links">
                {usefulLinks.map(link => (
                  <li key={link.path}>
                    <a href={link.path} onClick={e => { e.preventDefault(); navigate(link.path); }}>
                      {lang === 'fr' ? link.labelFr : link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer-col">
              <h4>{lang === 'fr' ? 'Contactez-nous' : 'Contact Us'}</h4>
              <div className="footer-contact-item">
                <div className="icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </div>
                <div className="text">
                  <strong>{lang === 'fr' ? 'Adresse du Parc' : 'Park Address'}</strong>
                  Kantaoui, Sousse, Tunisia
                </div>
              </div>
              <div className="footer-contact-item">
                <div className="icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                </div>
                <div className="text">
                  <strong>{lang === 'fr' ? 'Nous écrire' : 'Email Us'}</strong>
                  <a href="mailto:contact@hannibalpark.com" style={{ color: 'rgba(255,255,255,0.6)' }}>contact@hannibalpark.com</a>
                </div>
              </div>
              <div className="footer-contact-item">
                <div className="icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div className="text">
                  <strong>{lang === 'fr' ? 'Nous appeler' : 'Call Us'}</strong>
                  <a href="tel:+21626562000" style={{ color: 'rgba(255,255,255,0.6)' }}>+216 26 562 000</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="container">
            {lang === 'fr' 
              ? `© ${new Date().getFullYear()}, Hannibal Park Sousse - Tous droits réservés.` 
              : `© ${new Date().getFullYear()}, Hannibal Park Sousse - All Rights Reserved.`}
          </div>
        </div>
      </footer>
    </>
  );
}

// ============================================
// SCROLL TO TOP
// ============================================
function ScrollToTop() {
  const scrollY = useScrollPosition();
  return (
    <button
      className={`scroll-top ${scrollY > 400 ? 'visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
    >
      ↑
    </button>
  );
}

// ============================================
// PAGE: HOME
// ============================================
function HomePage({ navigate, lang, settings }) {
  return (
    <>
      <HeroSection navigate={navigate} lang={lang} settings={settings} />
      <AboutSection navigate={navigate} lang={lang} settings={settings} />
      <TicketsSection navigate={navigate} lang={lang} />
      <CategoriesSection navigate={navigate} lang={lang} />
      <LatestEventsSection navigate={navigate} lang={lang} />
      <FeaturesSection lang={lang} />
      <ReviewsSection lang={lang} />
      <GallerySection navigate={navigate} lang={lang} />
      <MapSection lang={lang} settings={settings} />
      <NewsSection navigate={navigate} lang={lang} />
    </>
  );
}

// ============================================
// PAGE: PLAN YOUR VISIT
// ============================================
function PlanVisitPage({ navigate, lang, settings }) {
  const getOpeningHours = () => {
    if (settings && settings.hours) {
      return lang === 'fr' 
        ? (settings.hours.opening_hours_fr || settings.hours.opening_hours_en)
        : settings.hours.opening_hours_en;
    }
    return lang === 'fr' ? 'Ouvert: 14h à 00h' : 'Open: 2PM to 10PM';
  };

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Planifier votre visite' : 'Plan Your Visit'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Planifier votre visite' : 'Plan your visit'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="about-grid">
              <div className="about-text">
                <div className="section-badge">{lang === 'fr' ? "À propos d'Hannibal Park" : "About Hannibal Park"}</div>
                <h2>{lang === 'fr' ? "Profitez d'un monde plus merveilleux à Hannibal Park" : "Enjoy a more wonderful world at Hannibal Park"}</h2>
                <p>
                  {lang === 'fr'
                    ? "Hannibal Park à Sousse Kantaoui est une destination vibrante qui promet un monde d'excitation et de joie pour les adultes et les enfants. Niché au cœur de la pittoresque Marina Kantaoui, ce parc d'attractions est plus qu'un simple lieu de jeu ; c'est un havre de paix où les souvenirs se créent et les rires résonnent."
                    : "Hannibal Park in Sousse Kantaoui is a vibrant destination that promises a world of excitement and joy for both adults and children. Nestled in the heart of the picturesque Marina Kantaoui, this amusement park is more than just a place to play; it's a haven where memories are made and laughter echoes through the air."}
                </p>
                <p>
                  {lang === 'fr'
                    ? "Avec plus de 32 manèges et jeux passionnants, Hannibal Park est un terrain de jeu de rêve. Des montagnes russes palpitantes qui vous feront crier de joie aux carrousels doux qui font sourire les plus petits, il y en a pour tous les goûts."
                    : "With over 32 thrilling rides and games, Hannibal Park is a playground of dreams. From heart-pounding roller coasters that make you scream with delight to gentle carousels that bring smiles to little ones' faces, there's something for everyone."}
                </p>
                <div className="hours-card">
                  <span className="icon"><MIcon name="schedule" /></span>
                  <div>
                    <div className="text">{lang === 'fr' ? "Heures d'ouverture" : "Opening Hours"}</div>
                    <div className="time">{getOpeningHours()}</div>
                  </div>
                </div>
              </div>
              <div className="about-image">
                <img src={SITE_IMAGES.heroChar} alt="Hannibal Park" style={{ borderRadius: '24px' }} />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section section-light" style={{ background: '#f8fafc' }}>
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <div className="section-badge" style={{ background: 'rgba(26, 175, 192, 0.08)', color: 'var(--hp-blue)' }}>
                {lang === 'fr' ? 'Transports' : 'Getting Here'}
              </div>
              <h2 className="section-title">
                {lang === 'fr' ? 'Comment venir à Hannibal Park ?' : 'How are you getting to Hannibal Park?'}
              </h2>
              <p className="section-subtitle">
                {lang === 'fr' 
                  ? 'Que vous choisissiez de conduire, de prendre un taxi ou les transports en commun, venir au parc est très simple.' 
                  : 'Whether you plan to drive or take public transport, getting to Hannibal Park is easy.'}
              </p>
            </div>

            <div className="transport-grid">
              {(lang === 'fr' ? [
                { icon: 'directions_car', title: 'En Voiture', desc: 'Utilisez Google Maps pour trouver facilement votre itinéraire, que vous veniez du sud ou du nord.', link: 'https://www.google.com/maps/place/Hannibal+Park/@35.8909279,10.5950348', linkText: 'Ouvrir Google Maps', color: '#3b82f6', bgLight: 'rgba(59, 130, 246, 0.08)' },
                { icon: 'local_taxi', title: 'En Taxi / Bolt', desc: 'Si vous êtes à Sousse, nous vous recommandons de réserver rapidement votre course via l\'application mobile Bolt.', link: 'https://play.google.com/store/apps/details?id=com.softylines.tjiktaxiclient', linkText: 'Télécharger l\'App Bolt', color: '#10b981', bgLight: 'rgba(16, 185, 129, 0.08)' },
                { icon: 'directions_bus', title: 'En Bus', desc: 'Vous pouvez vous déplacer avec les bus de la STS. Des lignes desservent régulièrement El Kantaoui.', link: 'http://data.transport.tn', linkText: 'Visiter le site STS', color: '#f59e0b', bgLight: 'rgba(245, 158, 11, 0.08)' },
              ] : [
                { icon: 'directions_car', title: 'By Car', desc: 'Use Google Maps to easily find your way to us, whether coming from the south or the north.', link: 'https://www.google.com/maps/place/Hannibal+Park/@35.8909279,10.5950348', linkText: 'Open in Google Maps', color: '#3b82f6', bgLight: 'rgba(59, 130, 246, 0.08)' },
                { icon: 'local_taxi', title: 'By Taxi / Bolt', desc: 'If you are in Sousse, we recommend making a quick request through the Bolt taxi mobile app.', link: 'https://play.google.com/store/apps/details?id=com.softylines.tjiktaxiclient', linkText: 'Get Bolt Taxi App', color: '#10b981', bgLight: 'rgba(16, 185, 129, 0.08)' },
                { icon: 'directions_bus', title: 'By Bus', desc: 'You can travel by STS bus. Routes are available everywhere to take you to Kantaoui.', link: 'http://data.transport.tn', linkText: 'Visit STS Website', color: '#f59e0b', bgLight: 'rgba(245, 158, 11, 0.08)' },
              ]).map((item, i) => (
                <div 
                  className="transport-card" 
                  key={i}
                  style={{ borderTop: `6px solid ${item.color}` }}
                >
                  <div 
                    className="transport-icon-wrap"
                    style={{ background: item.bgLight, color: item.color }}
                  >
                    <MIcon name={item.icon} size="2.5rem" />
                  </div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                  <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="transport-btn"
                    style={{ background: item.color }}
                  >
                    {item.linkText}
                  </a>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--hp-gradient-hero)', color: 'white', padding: '60px 0' }}>
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="number" style={{ color: 'var(--hp-yellow)' }}>32+</div>
              <div className="label" style={{ color: 'rgba(255,255,255,0.7)' }}>{lang === 'fr' ? 'Jeux' : 'Games'}</div>
            </div>
            <div className="stat-item">
              <div className="number" style={{ color: 'var(--hp-yellow)' }}>250+</div>
              <div className="label" style={{ color: 'rgba(255,255,255,0.7)' }}>{lang === 'fr' ? 'Spectacles & Événements chaque année' : 'Shows & Events Every Year'}</div>
            </div>
            <div className="stat-item">
              <div className="number" style={{ color: 'var(--hp-yellow)' }}>100k+</div>
              <div className="label" style={{ color: 'rgba(255,255,255,0.7)' }}>{lang === 'fr' ? 'Visiteurs chaque année' : 'Visitors Every Year'}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <div className="section-badge">{lang === 'fr' ? 'Règles du Parc' : 'Park Rules'}</div>
              <h2 className="section-title">{lang === 'fr' ? 'Des règles simples pour que Hannibal Park reste un plaisir pour tous' : 'Simple rules to keep Hannibal Park fun for everyone'}</h2>
            </div>
            <div className="rules-grid">
              {[
                { icon: 'checkroom', title: lang === 'fr' ? 'Veuillez vous habiller décemment' : 'Please dress modestly' },
                { icon: 'pets', title: lang === 'fr' ? 'Les animaux ne sont pas admis' : 'Pets & animals are not permitted' },
                { icon: 'favorite', title: lang === 'fr' ? 'Évitez les démonstrations d\'affection en public' : 'Avoid public display of affection' },
                { icon: 'skateboarding', title: lang === 'fr' ? 'Pas de segways, trottinettes, skateboards,...' : 'No segways, scooters, skateboards,...' },
              ].map((rule, i) => (
                <div className="rule-card" key={i}>
                  <div className="rule-icon"><MIcon name={rule.icon} size="2.5rem" /></div>
                  <h4>{rule.title}</h4>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: CONTACT
// ============================================
function ContactPage({ navigate, lang }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [captcha] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  });
  const [captchaInput, setCaptchaInput] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (parseInt(captchaInput) === captcha.answer) {
      setSubmitted(true);
      setForm({ name: '', email: '', message: '' });
      setCaptchaInput('');
    } else {
      alert(lang === 'fr' ? 'Captcha invalide. Veuillez réessayer.' : 'Invalid captcha. Please try again.');
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Contactez-nous' : 'Contact Us'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>Contact</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div>
              <div className="contact-info-cards">
                <div className="contact-info-card">
                  <div className="icon"><MIcon name="phone" size="1.8rem" /></div>
                  <div>
                    <strong>{lang === 'fr' ? 'Appelez-nous 7j/7 : ' : 'Call Us 7j/7 : '}</strong>
                    <a href="tel:+21626562000">+216 26 562 000</a>
                  </div>
                </div>
                <div className="contact-info-card">
                  <div className="icon"><MIcon name="email" size="1.8rem" /></div>
                  <div>
                    <strong>{lang === 'fr' ? 'Notre Email : ' : 'Our Email : '}</strong>
                    <a href="mailto:contact@hannibalpark.com">contact@hannibalpark.com</a>
                  </div>
                </div>
                <div className="contact-info-card">
                  <div className="icon"><MIcon name="location_on" size="1.8rem" /></div>
                  <div>
                    <strong>{lang === 'fr' ? 'Emplacement : ' : 'Location : '}</strong>
                    Kantaoui, Sousse, Tunisia
                  </div>
                </div>
              </div>

              <div className="map-container" style={{ height: '300px', borderRadius: '16px', overflow: 'hidden' }}>
                <iframe
                  title="Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3221.8!2d10.5950348!3d35.8909279!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fd898d7d16fe65%3A0xc87c1532803ea9d9!2sHannibal%20Park!5e0!3m2!1sen!2stn"
                  allowFullScreen
                  loading="lazy"
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              </div>
            </div>

            <div className="contact-form">
              <h2 style={{ marginBottom: '8px' }}>{lang === 'fr' ? 'Prêt à commencer ?' : 'Ready to get started?'}</h2>
              <p style={{ color: 'var(--hp-gray-500)', marginBottom: '28px' }}>
                {lang === 'fr'
                  ? "Vous avez une réclamation ou une demande d'information ? N'hésitez pas à remplir ce formulaire."
                  : "Have a complaint or a request for information? Feel free to fill out this form."}
              </p>

              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px', background: 'var(--hp-blue-light)', borderRadius: '16px' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '16px', color: '#4CAF50' }}><MIcon name="check_circle" size="3rem" /></div>
                  <h3>{lang === 'fr' ? 'Message envoyé !' : 'Message Sent!'}</h3>
                  <p style={{ color: 'var(--hp-gray-500)' }}>{lang === 'fr' ? "Nous vous répondrons dans les plus brefs délais." : "We'll get back to you soon."}</p>
                  <button className="btn-primary" style={{ marginTop: '16px' }} onClick={() => setSubmitted(false)}>
                    {lang === 'fr' ? 'Envoyer un autre message' : 'Send Another Message'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>{lang === 'fr' ? 'Votre nom *' : 'Your Name *'}</label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>{lang === 'fr' ? 'Votre e-mail *' : 'Your Email *'}</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required autoComplete="email" />
                  </div>
                  <div className="form-group">
                    <label>{lang === 'fr' ? 'Votre message *' : 'Your Message *'}</label>
                    <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>{lang === 'fr' ? `Contrôle de sécurité : ${captcha.a} + ${captcha.b} = ?` : `Security Check: ${captcha.a} + ${captcha.b} = ?`}</label>
                    <input type="number" value={captchaInput} onChange={e => setCaptchaInput(e.target.value)} required placeholder={lang === 'fr' ? "Résolvez ceci pour prouver que vous n'êtes pas un robot" : "Solve this to prove you're not a robot"} />
                  </div>
                  <button type="submit" className="form-submit">{lang === 'fr' ? 'Envoyer le message' : 'Send Message'}</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: ATTRACTIONS
// ============================================
function AttractionsPage({ navigate, lang }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCategories()
      .then(data => {
        setCategories(data.categories || []);
      })
      .catch(err => {
        console.error("Failed to load categories:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const getCategoryImageUrl = (cat) => {
    if (!cat.image) return 'https://www.hannibalpark.com/assets/img/categories/category_68dd041069458.jpg';
    if (cat.image.startsWith('http://') || cat.image.startsWith('https://') || cat.image.startsWith('/')) {
      return cat.image;
    }
    return `${BASE_SERVER_URL}/uploads/categories/${cat.image}`;
  };

  const listToRender = categories.length > 0 ? categories : CATEGORIES;

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Attractions & Pavillons' : 'Attractions & Pavillons'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <span>Attractions</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', minHeight: '300px' }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="cat-skeleton" style={{ height: '320px', borderRadius: '24px' }} />
              ))}
            </div>
          ) : (
            <div className="categories-grid">
              {listToRender.map((cat, i) => {
                const name = lang === 'fr' ? (cat.name_fr || cat.name_en) : cat.name_en;
                return (
                  <AnimatedSection key={cat.id || i}>
                    <div className="category-card" onClick={() => navigate(`/category/${cat.slug}`)}>
                      <img src={getCategoryImageUrl(cat)} alt={name} />
                      <div className="category-overlay">
                        <h3>{name}</h3>
                        <span className="link">{lang === 'fr' ? 'Plus de détails →' : 'More Details →'}</span>
                      </div>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          )}

          <AnimatedSection>
            <div style={{ marginTop: '60px', padding: '40px', background: 'var(--hp-blue-light)', borderRadius: '24px' }}>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--hp-gray-700)' }}>
                {lang === 'fr' 
                  ? "Hannibal Park est la destination idéale pour créer des moments magiques en famille ou entre amis. Conçu pour ravir les enfants, captiver les adolescents et divertir les adultes, le parc propose une large gamme d'activités pour tous les âges. Pour les plus petits, des attractions colorées et sécurisées stimulent leur imagination. Les adolescents peuvent faire le plein d'adrénaline avec des manèges à sensations fortes et des défis passionnants. Les adultes peuvent se détendre dans une atmosphère accueillante, avec des espaces dédiés à la relaxation, à la découverte culinaire et à des activités interactives."
                  : "Hannibal Park is the perfect destination for creating magical moments with family or friends. Designed to delight children, captivate teens, and entertain adults, the park offers a wide range of activities for all ages. For the little ones, colorful and safe attractions spark their imagination. Teens can get their adrenaline rush with thrilling rides and exciting challenges. Adults can unwind in a welcoming atmosphere, with areas dedicated to relaxation, culinary exploration, and interactive activities."
                }
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: GALLERY
// ============================================
function GalleryPage({ navigate, lang }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getGallery()
      .then(res => {
        setItems(res.gallery || []);
      })
      .catch(err => {
        console.error("Failed to load gallery items:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const getGalleryMediaUrl = (item) => {
    if (!item.image) return '';
    if (item.image.startsWith('http://') || item.image.startsWith('https://') || item.image.startsWith('data:')) {
      return item.image;
    }
    const imageName = item.image.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/gallery/${imageName}`;
  };

  const hasItems = items.length > 0;
  const listToRender = hasItems ? items : GALLERY_IMAGES.map((img, i) => ({
    id: i,
    image: img,
    type: 'image',
    title_en: `Gallery ${i + 1}`,
    title_fr: `Galerie ${i + 1}`
  }));

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Notre Galerie' : 'Our Gallery'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Galerie' : 'Gallery'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading && !hasItems ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px', minHeight: '300px' }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="cat-skeleton" style={{ height: '240px', borderRadius: '24px' }} />
              ))}
            </div>
          ) : (
            <div className="gallery-grid">
              {listToRender.map((item, i) => {
                const mediaUrl = getGalleryMediaUrl(item);
                const isVideo = item.type === 'video';
                const title = lang === 'fr' ? (item.title_fr || item.title_en) : item.title_en;

                return (
                  <div 
                    className="gallery-item" 
                    key={item.id || i} 
                    style={{ 
                      position: 'relative', 
                      overflow: 'hidden', 
                      borderRadius: '24px', 
                      aspectRatio: '4/3',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                      background: '#1a1a2e',
                      border: '1px solid var(--hp-blue-light)'
                    }}
                  >
                    {isVideo ? (
                      <video 
                        src={mediaUrl} 
                        controls 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                      />
                    ) : (
                      <img 
                        src={mediaUrl} 
                        alt={title || `Gallery Image`} 
                        loading="lazy" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                      />
                    )}
                    {title && !isVideo && (
                      <div 
                        className="gallery-overlay"
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          padding: '20px',
                          background: 'linear-gradient(transparent, rgba(26,26,46,0.9))',
                          color: 'white',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          display: 'flex',
                          alignItems: 'flex-end'
                        }}
                      >
                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>{title}</h4>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: PARK RULES
// ============================================
function ParkRulesPage({ navigate, lang }) {
  const rules = lang === 'fr' ? [
    { icon: 'checkroom', title: 'Veuillez vous habiller décemment', desc: 'Nous prions nos visiteurs de porter une tenue décente pendant leur visite.' },
    { icon: 'pets', title: 'Les animaux de compagnie ne sont pas admis', desc: 'Pour la sécurité et le confort de tous, les animaux de compagnie ne sont pas autorisés dans le parc.' },
    { icon: 'favorite', title: 'Évitez les démonstrations d\'affection en public', desc: 'Nous demandons à nos visiteurs de respecter les règles de bienséance dans cet environnement familial.' },
    { icon: 'skateboarding', title: 'Pas de segways, trottinettes, skateboards,...', desc: 'Ces articles ne sont pas autorisés dans le parc pour des raisons de sécurité.' },
    { icon: 'photo_camera', title: 'La photographie professionnelle nécessite une autorisation', desc: 'Veuillez nous contacter avant d\'apporter du matériel professionnel.' },
    { icon: 'no_food', title: 'Pas de nourriture ni de boissons extérieures', desc: 'Nos restaurants et cafés proposent une variété d\'options de restauration.' },
    { icon: 'child_care', title: 'Les enfants doivent être surveillés', desc: 'Les enfants de moins de 12 ans doivent être accompagnés d\'un adulte en tout temps.' },
    { icon: 'smoke_free', title: 'Ne pas fumer en dehors des zones désignées', desc: 'Il est interdit de fumer en dehors des zones prévues à cet effet.' },
  ] : [
    { icon: 'checkroom', title: 'Please dress modestly', desc: 'We kindly ask all guests to wear appropriate attire during their visit.' },
    { icon: 'pets', title: 'Pets & animals are not permitted', desc: 'For the safety and comfort of all guests, pets are not allowed in the park.' },
    { icon: 'favorite', title: 'Avoid public display of affection', desc: 'We ask guests to be mindful of public behavior in this family-friendly environment.' },
    { icon: 'skateboarding', title: 'No segways, scooters, skateboards,...', desc: 'These items are not allowed in the park for safety reasons.' },
    { icon: 'photo_camera', title: 'Professional photography requires permission', desc: 'Please contact us before bringing professional equipment.' },
    { icon: 'no_food', title: 'No outside food or beverages', desc: 'Our restaurants and cafes offer a variety of dining options.' },
    { icon: 'child_care', title: 'Children must be supervised', desc: 'Children under 12 must be accompanied by an adult at all times.' },
    { icon: 'smoke_free', title: 'No smoking in designated areas', desc: 'Smoking is only permitted in designated smoking areas.' },
  ];

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Règles du Parc' : 'Park Rules'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Règles du Parc' : 'Park Rules'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2 className="section-title">{lang === 'fr' ? 'Des règles simples pour que Hannibal Park reste un plaisir pour tous' : 'Simple rules to keep Hannibal Park fun for everyone'}</h2>
              <p className="section-subtitle">{lang === 'fr' ? 'Veuillez suivre ces règles afin de garantir une expérience sûre et agréable pour tous.' : 'Please follow these rules to ensure a safe and enjoyable experience for all visitors.'}</p>
            </div>
          </AnimatedSection>

          <div className="rules-grid">
            {rules.map((rule, i) => (
              <AnimatedSection key={i}>
                <div className="rule-card">
                  <div className="rule-icon"><MIcon name={rule.icon} size="2.5rem" /></div>
                  <h4>{rule.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--hp-gray-500)', marginTop: '8px' }}>{rule.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: EVENTS
// ============================================
function EventsPage({ navigate, lang }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.getEvents()
      .then(data => setEvents(data.events || []))
      .catch(err => setError(err.message || 'Failed to load events'))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatTime = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':');
    return `${h}h${m}`;
  };

  const getImageUrl = (ev) => {
    if (!ev.image) return null;
    if (ev.image_url && ev.image_url.startsWith('http')) {
      return ev.image_url;
    }
    const imageName = ev.image.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/events/${imageName}`;
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.event_date >= today);
  const past     = events.filter(e => e.event_date <  today);

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Spectacles & Événements' : 'Shows & Events'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Événements' : 'Events'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">

          {loading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: '28px' }}>
              {[1,2,3].map(i => (
                <div key={i} className="cat-skeleton" style={{ height: '300px', borderRadius: '20px' }} />
              ))}
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}><MIcon name="warning" size="3rem" style={{ color: '#f59e0b' }} /></div>
              <h3>{lang === 'fr' ? 'Erreur de chargement' : 'Failed to load events'}</h3>
              <p style={{ color: 'var(--hp-gray-500)' }}>{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {upcoming.length > 0 && (
                <AnimatedSection>
                  <div className="section-header">
                    <div className="section-badge"><MIcon name="celebration" /> {lang === 'fr' ? 'À venir' : 'Upcoming'}</div>
                    <h2 className="section-title">
                      {lang === 'fr' ? 'Prochains événements' : 'Upcoming Events'}
                    </h2>
                    <p className="section-subtitle">
                      {lang === 'fr'
                        ? "Ne manquez pas nos spectacles et événements à venir !"
                        : "Don't miss our upcoming shows and events!"}
                    </p>
                  </div>
                  <div className="ev-grid">
                    {upcoming.map((ev, i) => {
                      const title  = lang === 'fr' ? (ev.title_fr || ev.title_en) : ev.title_en;
                      const desc   = lang === 'fr' ? (ev.description_fr || ev.description_en) : ev.description_en;
                      const imgUrl = getImageUrl(ev);
                      return (
                        <AnimatedSection key={ev.id || i}>
                          <article className="ev-card">
                            <div className={`ev-img ${!imgUrl ? 'ev-img-placeholder' : ''}`}>
                              {imgUrl
                                ? <img src={imgUrl} alt={title} loading="lazy" />
                                : <span><MIcon name="celebration" size="4rem" /></span>}
                              {ev.is_featured == 1 && (
                                <div className="ev-featured"><MIcon name="star" /> {lang === 'fr' ? 'Vedette' : 'Featured'}</div>
                              )}
                            </div>
                            <div className="ev-body">
                              <div className="ev-meta">
                                <span className="ev-date-badge"><MIcon name="calendar_today" /> {formatDate(ev.event_date)}</span>
                                {ev.event_time && <span className="ev-time-badge"><MIcon name="schedule" /> {formatTime(ev.event_time)}</span>}
                              </div>
                              <h3 className="ev-title">{title}</h3>
                              {desc && <p className="ev-desc">{desc}</p>}
                              {ev.location && <div className="ev-loc"><MIcon name="location_on" /> {ev.location}</div>}
                            </div>
                          </article>
                        </AnimatedSection>
                      );
                    })}
                  </div>
                </AnimatedSection>
              )}

              {upcoming.length === 0 && past.length === 0 && (
                <div style={{ textAlign: 'center', padding: '80px 0' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}><MIcon name="celebration" /></div>
                  <h2>{lang === 'fr' ? 'Aucun événement pour le moment' : 'No Events at the Moment'}</h2>
                  <p style={{ color: 'var(--hp-gray-500)', maxWidth: '480px', margin: '12px auto' }}>
                    {lang === 'fr'
                      ? "Restez à l'écoute ! De nouveaux événements seront annoncés prochainement."
                      : 'Stay tuned! New events will be announced soon.'}
                  </p>
                </div>
              )}

              {past.length > 0 && (
                <AnimatedSection style={{ marginTop: upcoming.length > 0 ? '60px' : 0 }}>
                  <div className="section-header">
                    <div className="section-badge" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                      <MIcon name="history" /> {lang === 'fr' ? 'Passés' : 'Past'}
                    </div>
                    <h2 className="section-title" style={{ color: 'var(--hp-gray-500)' }}>
                      {lang === 'fr' ? 'Événements passés' : 'Past Events'}
                    </h2>
                  </div>
                  <div className="ev-grid">
                    {past.map((ev, i) => {
                      const title  = lang === 'fr' ? (ev.title_fr || ev.title_en) : ev.title_en;
                      const imgUrl = getImageUrl(ev);
                      return (
                        <AnimatedSection key={ev.id || i}>
                          <article className="ev-card ev-card-past">
                            <div className={`ev-img ev-img-past ${!imgUrl ? 'ev-img-placeholder' : ''}`}>
                              {imgUrl ? <img src={imgUrl} alt={title} loading="lazy" /> : <span><MIcon name="celebration" size="4rem" /></span>}
                              <div className="ev-ended-label"><MIcon name="block" /> {lang === 'fr' ? 'Terminé' : 'Ended'}</div>
                            </div>
                            <div className="ev-body">
                              <div className="ev-meta">
                                <span className="ev-date-badge ev-date-past"><MIcon name="calendar_today" /> {formatDate(ev.event_date)}</span>
                                <span className="ev-ended-badge"><MIcon name="radio_button_checked" /> {lang === 'fr' ? 'Événement Terminé' : 'Event Ended'}</span>
                              </div>
                              <h3 className="ev-title" style={{ color: 'var(--hp-gray-500)' }}>{title}</h3>
                              {ev.location && <div className="ev-loc" style={{ color: '#9ca3af' }}><MIcon name="location_on" /> {ev.location}</div>}
                            </div>
                          </article>
                        </AnimatedSection>
                      );
                    })}
                  </div>
                </AnimatedSection>
              )}
            </>
          )}

        </div>
      </section>

      <style>{`
        .ev-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:28px; margin-top:40px; }
        .ev-card { background:white; border-radius:20px; overflow:hidden; box-shadow:0 4px 24px rgba(0,0,0,.08); transition:transform .3s,box-shadow .3s; }
        .ev-card:hover { transform:translateY(-6px); box-shadow:0 12px 40px rgba(0,0,0,.14); }
        .ev-card-past { opacity:.72; filter:grayscale(.3); }
        .ev-img { position:relative; height:200px; overflow:hidden; }
        .ev-img img { width:100%; height:100%; object-fit:cover; transition:transform .4s; }
        .ev-card:hover .ev-img img { transform:scale(1.05); }
        .ev-img-placeholder { background:linear-gradient(135deg,#1a1a2e,#2bb5e0); display:flex; align-items:center; justify-content:center; font-size:4rem; }
        .ev-img-past { filter:grayscale(.5); }
        .ev-featured { position:absolute; top:12px; left:12px; background:linear-gradient(135deg,#f59e0b,#ef4444); color:white; font-size:11px; font-weight:700; padding:4px 10px; border-radius:20px; }
        .ev-ended-label { position:absolute; top:12px; right:12px; background:rgba(220,38,38,0.9); backdrop-filter:blur(4px); color:white; font-size:11px; font-weight:700; padding:4px 10px; border-radius:20px; box-shadow:0 2px 8px rgba(0,0,0,0.15); z-index:5; }
        .ev-body { padding:20px 22px; }
        .ev-meta { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
        .ev-date-badge { display:inline-flex; align-items:center; gap:5px; background:#eff6ff; color:#1d4ed8; font-size:12px; font-weight:600; padding:4px 10px; border-radius:6px; }
        .ev-date-past { background:#f3f4f6; color:#6b7280; }
        .ev-time-badge { display:inline-flex; align-items:center; gap:5px; background:#f0fdf4; color:#166534; font-size:12px; font-weight:600; padding:4px 10px; border-radius:6px; }
        .ev-ended-badge { display:inline-flex; align-items:center; gap:5px; background:#fef2f2; color:#dc2626; font-size:12px; font-weight:700; padding:4px 10px; border-radius:6px; border:1px solid #fee2e2; }
        .ev-title { font-size:1.1rem; font-weight:700; margin:0 0 8px; color:#111827; line-height:1.4; }
        .ev-desc { font-size:.88rem; color:#6b7280; line-height:1.6; margin:0 0 10px; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
        .ev-loc { font-size:12px; color:#6b7280; font-weight:600; }
        @media(max-width:640px) { .ev-grid { grid-template-columns:1fr; } }
      `}</style>
    </>
  );
}

// ============================================
// PAGE: NEWS
// ============================================
function NewsPage({ navigate, lang }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.getNews(page)
      .then(data => {
        setNews(data.news || []);
        setTotalPages(data.pages || 1);
      })
      .catch(err => console.error("Failed to load news:", err))
      .finally(() => setLoading(false));
  }, [page]);

  const getImageUrl = (n) => {
    if (!n.image) return 'https://www.hannibalpark.com/assets/img/news/recent-winter-holiday.jpg';
    if (n.image.startsWith('http')) return n.image;
    const imageName = n.image.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/news/${imageName}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Actualités et articles' : 'News & Articles'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Actualités' : 'News'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading ? (
             <div style={{ textAlign: 'center', padding: '40px', color: 'var(--hp-gray-500)' }}>{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>
          ) : news.length === 0 ? (
             <div style={{ textAlign: 'center', padding: '40px', color: 'var(--hp-gray-500)' }}>{lang === 'fr' ? 'Aucune actualité.' : 'No news found.'}</div>
          ) : (
            <>
              <div className="news-grid">
                {news.map(n => {
                  const title = lang === 'fr' ? (n.title_fr || n.title_en) : n.title_en;
                  const exc = lang === 'fr' ? (n.excerpt_fr || n.excerpt_en) : n.excerpt_en;
                  return (
                    <div className="news-card" key={n.id} onClick={() => navigate(`/news/${n.slug}`)} style={{ cursor: 'pointer' }}>
                      <div className="news-card-image">
                        <img src={getImageUrl(n)} alt={title} />
                      </div>
                      <div className="news-card-body">
                        <div className="news-card-date">{formatDate(n.published_at)}</div>
                        <h4>{title}</h4>
                        <p>{exc}</p>
                        <span className="read-more">{lang === 'fr' ? 'Lire la suite →' : 'Read More →'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '40px' }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button 
                      key={p} 
                      onClick={() => setPage(p)}
                      style={{ 
                        padding: '8px 16px', 
                        borderRadius: '6px', 
                        border: 'none',
                        background: p === page ? 'var(--hp-blue)' : '#f3f4f6',
                        color: p === page ? 'white' : 'var(--hp-gray-700)',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <style>{`
        .news-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:24px; }
        .news-card { background:white; border-radius:12px; overflow:hidden; box-shadow:0 4px 12px rgba(0,0,0,0.05); transition:transform 0.3s; display:flex; flex-direction:column; }
        .news-card:hover { transform:translateY(-5px); box-shadow:0 12px 24px rgba(0,0,0,0.1); }
        .news-card-image { height:220px; overflow:hidden; }
        .news-card-image img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s; }
        .news-card:hover .news-card-image img { transform:scale(1.05); }
        .news-card-body { padding:24px; flex-grow:1; display:flex; flex-direction:column; }
        .news-card-date { color:var(--hp-blue); font-weight:bold; font-size:0.9rem; margin-bottom:8px; }
        .news-card-body h4 { font-size:1.2rem; margin:0 0 12px; line-height:1.4; color:#111827; }
        .news-card-body p { color:var(--hp-gray-500); font-size:0.95rem; line-height:1.6; margin-bottom:16px; flex-grow:1; }
        .read-more { color:var(--hp-blue); font-weight:bold; font-size:0.9rem; }
      `}</style>
    </>
  );
}

// ============================================
// PAGE: NEWS ARTICLE DETAIL
// ============================================
function NewsArticlePage({ navigate, lang, slug }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestNews, setLatestNews] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.getNewsArticle(slug)
      .then(data => {
        if (data.article) {
          setArticle(data.article);
        } else {
          setError(lang === 'fr' ? 'Article non trouvé' : 'Article not found');
        }
      })
      .catch(err => {
        setError(lang === 'fr' ? 'Erreur lors du chargement' : 'Error loading article');
        console.error("Failed to load article:", err);
      })
      .finally(() => setLoading(false));

    api.getNews(1)
      .then(data => {
        const allNews = data.news || [];
        const filtered = allNews.filter(n => n.slug !== slug).slice(0, 3);
        setLatestNews(filtered.length > 0 ? filtered : allNews.slice(0, 3));
      })
      .catch(err => console.error("Failed to load latest news:", err));
  }, [slug]);

  const getImageUrl = (img) => {
    if (!img) return 'https://www.hannibalpark.com/assets/img/news/recent-winter-holiday.jpg';
    if (img.startsWith('http')) return img;
    const imageName = img.split('/').pop();
    return `${BASE_SERVER_URL}/uploads/news/${imageName}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: 'var(--hp-gray-500)' }}>
        <MIcon name="hourglass_empty" size="3rem" style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
        <p style={{ marginTop: '16px' }}>{lang === 'fr' ? 'Chargement...' : 'Loading...'}</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div style={{ marginBottom: '16px' }}><MIcon name="article" size="4rem" /></div>
        <h2>{error || 'Article not found'}</h2>
        <a href="/news" className="btn-primary" onClick={e => { e.preventDefault(); navigate('/news'); }} style={{ marginTop: '24px', display: 'inline-block' }}>
          {lang === 'fr' ? 'Retour aux actualités' : 'Back to News'}
        </a>
      </div>
    );
  }

  const title = lang === 'fr' ? (article.title_fr || article.title_en) : article.title_en;
  const content = lang === 'fr' ? (article.content_fr || article.content_en) : article.content_en;

  return (
    <>
      <div className="page-header" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url(${getImageUrl(article.image)})` }}>
        <div className="container" style={{ maxWidth: '800px', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: 'var(--hp-blue)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '16px' }}>
            {formatDate(article.published_at)}
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', margin: '0 0 16px', lineHeight: '1.2' }}>{title}</h1>
          <div className="breadcrumb" style={{ justifyContent: 'center' }}>
            <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
            <span>/</span>
            <a href="/news" onClick={e => { e.preventDefault(); navigate('/news'); }}>{lang === 'fr' ? 'Actualités' : 'News'}</a>
          </div>
        </div>
      </div>

      <section className="section" style={{ padding: '60px 0', background: '#f8fafc' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div className="article-layout">
            
            {/* Left/Main Column: Article content */}
            <div className="article-main">
              <div className="article-content" style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#374151' }}>
                {content.split('\n').map((paragraph, idx) => (
                  paragraph.trim() ? <p key={idx} style={{ marginBottom: '24px' }}>{paragraph}</p> : null
                ))}
              </div>

              {article.author_name && (
                <div style={{ marginTop: '40px', padding: '24px', background: 'var(--hp-gray-50)', borderRadius: '12px', borderLeft: '4px solid var(--hp-blue)' }}>
                  <strong>{lang === 'fr' ? 'Auteur:' : 'Author:'}</strong> {article.author_name}
                </div>
              )}
            </div>

            {/* Right Column: Sidebar */}
            <aside className="article-sidebar">
              {/* Section 1: Back to News */}
              <div className="sidebar-widget back-widget">
                <button onClick={() => navigate('/news')} className="btn-sidebar-back">
                  {lang === 'fr' ? '← Retour aux actualités' : '← Back to News'}
                </button>
              </div>

              {/* Section 2: Latest News */}
              {latestNews.length > 0 && (
                <div className="sidebar-widget latest-widget">
                  <h3>{lang === 'fr' ? 'Dernières Actualités' : 'Latest News'}</h3>
                  <div className="sidebar-latest-list">
                    {latestNews.map(n => {
                      const nTitle = lang === 'fr' ? (n.title_fr || n.title_en) : n.title_en;
                      return (
                        <div className="sidebar-latest-item" key={n.id} onClick={() => navigate(`/news/${n.slug}`)}>
                          <div className="sidebar-latest-img">
                            <img src={getImageUrl(n.image)} alt={nTitle} />
                          </div>
                          <div className="sidebar-latest-info">
                            <div className="sidebar-latest-date">{formatDate(n.published_at)}</div>
                            <h4>{nTitle}</h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </aside>

          </div>
        </div>
      </section>

      <style>{`
        .article-layout {
          display: grid;
          grid-template-columns: 2.2fr 1fr;
          gap: 48px;
          align-items: start;
        }
        @media (max-width: 991px) {
          .article-layout {
            grid-template-columns: 1fr;
            gap: 40px;
          }
        }
        .article-main {
          background: white;
          padding: 40px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          border: 1px solid rgba(0, 0, 0, 0.03);
        }
        @media (max-width: 640px) {
          .article-main {
            padding: 24px;
          }
        }
        .article-sidebar {
          display: flex;
          flex-direction: column;
          gap: 28px;
          position: sticky;
          top: 100px;
        }
        .sidebar-widget {
          background: white;
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          border: 1px solid rgba(0, 0, 0, 0.03);
        }
        .back-widget {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          color: white;
          text-align: center;
        }
        .btn-sidebar-back {
          background: rgba(255, 255, 255, 0.15);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 50px;
          padding: 12px 24px;
          font-weight: 700;
          cursor: pointer;
          width: 100%;
          text-align: center;
          transition: all 0.3s ease;
          font-family: inherit;
        }
        .btn-sidebar-back:hover {
          background: white;
          color: #1a1a2e;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        .latest-widget h3 {
          font-size: 1.2rem;
          font-weight: 800;
          margin: 0 0 20px 0;
          color: #111827;
          position: relative;
          padding-bottom: 10px;
          border-bottom: 2px solid var(--hp-blue);
        }
        .sidebar-latest-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .sidebar-latest-item {
          display: flex;
          gap: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          align-items: center;
        }
        .sidebar-latest-item:hover {
          transform: translateX(4px);
        }
        .sidebar-latest-img {
          width: 72px;
          height: 72px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .sidebar-latest-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .sidebar-latest-info {
          flex-grow: 1;
        }
        .sidebar-latest-date {
          font-size: 0.8rem;
          color: var(--hp-blue);
          font-weight: 700;
          margin-bottom: 4px;
        }
        .sidebar-latest-info h4 {
          font-size: 0.95rem;
          font-weight: 700;
          margin: 0;
          color: #1f2937;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .sidebar-latest-item:hover h4 {
          color: var(--hp-blue);
        }
      `}</style>
    </>
  );
}

function BuyTicketsPage({ navigate, lang }) {
  const [plans, setPlans] = useState([]);
  const [totalTickets, setTotalTickets] = useState(999);
  const [totalPlans, setTotalPlans] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.getTicketPlans()
      .then(data => {
        setPlans(data.plans || []);
        if (data.total_tickets !== undefined) setTotalTickets(data.total_tickets);
        if (data.total_plans !== undefined) setTotalPlans(data.total_plans);
      })
      .catch(err => {
        console.error("Failed to load ticket plans:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const getParsedFeatures = (plan) => {
    let parsedFeatures = [];
    try {
      parsedFeatures = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
    } catch (e) {
      parsedFeatures = [];
    }
    if (parsedFeatures.length === 0) {
      parsedFeatures.push(lang === 'fr' ? `Obtenez ${plan.points} Points` : `Get ${plan.points} Points`);
      parsedFeatures.push(lang === 'fr' ? `Accès libre pendant ${plan.validity_days} jour(s)` : `Enjoy ${plan.validity_days} Day(s) of free access`);
      if (plan.skip_queue) {
        parsedFeatures.push(lang === 'fr' ? 'Accès coupe-file inclus' : 'No waiting queue (Skip Queue)');
      }
      if (plan.games_range) {
        parsedFeatures.push(lang === 'fr' ? `Gamme de jeux : ${plan.games_range}` : `Games range: ${plan.games_range}`);
      }
    }
    return parsedFeatures;
  };

  const fallbackPlans = TICKET_PLANS.map(p => ({
    id: null,
    name_en: p.name,
    name_fr: p.name,
    price: p.price,
    currency: p.currency,
    is_popular: p.popular,
    features: p.features
  }));

  const listToRender = plans.length > 0 ? plans : fallbackPlans;

  const handleSelectPlan = (plan, i) => {
    sessionStorage.setItem('hp_selected_plan', JSON.stringify(plan));
    if (user) {
      navigate('/checkout');
    } else {
      sessionStorage.setItem('redirect_after_login', '/checkout');
      navigate('/login');
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Acheter des Tickets' : 'Buy Tickets'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Acheter des Tickets' : 'Buy Tickets'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <h2 className="section-title">
                {lang === 'fr' ? 'Sélectionnez votre Pass' : 'Select Your Pass'}
              </h2>
              <p className="section-subtitle">
                {lang === 'fr' 
                  ? 'Choisissez le plan parfait et procédez au paiement sécurisé via ClicToPay' 
                  : 'Choose the perfect plan and proceed to secure payment via ClicToPay'}
              </p>
            </div>
          </AnimatedSection>

          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', minHeight: '300px', marginBottom: '60px' }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="cat-skeleton" style={{ height: '380px', borderRadius: '24px' }} />
              ))}
            </div>
          ) : (
            <div className="tickets-grid" style={{ marginBottom: '60px' }}>
              {listToRender.map((plan, i) => {
                const name = lang === 'fr' ? (plan.name_fr || plan.name_en) : plan.name_en;
                const features = getParsedFeatures(plan);
                const isPopular = plan.is_popular;
                const isOutOfStock = totalTickets < totalPlans;
                return (
                  <div
                    key={plan.id || i}
                    className={`ticket-card`}
                  >
                    {!!isPopular && <div className="ticket-popular-badge">{lang === 'fr' ? 'Le Plus Populaire' : 'Most Popular'}</div>}
                    <div className="ticket-name">{name}</div>
                    <div className="ticket-price">{plan.price}<span>{plan.currency || 'TND'}</span></div>
                    
                    <div className={`ticket-stock ${isOutOfStock ? 'out-of-stock' : 'in-stock'}`} style={{ textAlign: 'center', margin: '10px 0', fontWeight: 'bold', color: isOutOfStock ? '#dc2626' : '#16a34a' }}>
                      {isOutOfStock 
                        ? (lang === 'fr' ? 'Rupture de Stock' : 'Out Of Stock') 
                        : (lang === 'fr' ? 'Stock Disponible' : 'Available Stock')}
                    </div>

                    <ul className="ticket-features">
                      {features.map((f, j) => (
                        <li key={j}><span className="check">✓</span> {f}</li>
                      ))}
                    </ul>
                    {isOutOfStock ? (
                      <button className="ticket-btn" style={{ background: '#9ca3af', cursor: 'not-allowed' }} disabled>
                        {lang === 'fr' ? 'Rupture de Stock' : 'Out Of Stock'}
                      </button>
                    ) : (
                      <button onClick={() => handleSelectPlan(plan, i)} className="ticket-btn" style={{ background: 'var(--hp-gradient-blue)' }}>
                        {lang === 'fr' ? 'Choisir ce plan' : 'Select This Plan'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: CHECKOUT (AUTHENTICATED ONLY)
// ============================================
function CheckoutPage({ navigate, lang }) {
  const { user, loading: authLoading } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [totalTickets, setTotalTickets] = useState(999);
  const [totalPlans, setTotalPlans] = useState(0);

  useEffect(() => {
    api.getTicketPlans().then(data => {
      if (data.total_tickets !== undefined) setTotalTickets(data.total_tickets);
      if (data.total_plans !== undefined) setTotalPlans(data.total_plans);
    }).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const savedPlan = sessionStorage.getItem('hp_selected_plan');
    if (savedPlan) {
      try {
        setSelectedPlan(JSON.parse(savedPlan));
      } catch (e) {
        navigate('/buy-tickets');
      }
    } else {
      navigate('/buy-tickets');
    }
  }, [user, navigate, authLoading]);

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!selectedPlan || !user) return;
    
    const isOutOfStock = totalTickets < totalPlans;
    if (isOutOfStock) {
      alert(lang === 'fr' ? 'Rupture de stock.' : 'Out of stock.');
      return;
    }

    const isQuantityInvalid = isOutOfStock || quantity > totalTickets;
    if (isQuantityInvalid) {
      alert(lang === 'fr' ? 'Quantité indisponible.' : 'Quantité indisponible.');
      return;
    }

    setProcessing(true);

    const purchaseData = {
      plan_id: selectedPlan.id,
      quantity: quantity,
      payment_method: 'clictopay'
    };

    if (!selectedPlan.id) {
      setTimeout(() => {
        alert(
          lang === 'fr' 
             ? `Commande créée ! Total : ${(parseFloat(selectedPlan.price) * quantity).toFixed(2)} TND\nSimulation de paiement...` 
            : `Order created! Total: ${(parseFloat(selectedPlan.price) * quantity).toFixed(2)} TND\nSimulating payment...`
        );
        sessionStorage.removeItem('hp_selected_plan');
        setProcessing(false);
        navigate('/profile');
      }, 1500);
      return;
    }

    api.purchaseTicket(purchaseData)
      .then(res => {
        const order = res.order;
        api.initPayment({
          order_number: order.order_number,
          amount: order.total_amount,
          currency: 'TND',
          email: user.email,
          name: `${user.first_name} ${user.last_name}`,
          phone: user.phone || ''
        })
        .then(payRes => {
          sessionStorage.removeItem('hp_selected_plan');
          if (payRes.payment_url) {
            window.location.href = payRes.payment_url;
          } else {
            navigate(`/payment/callback?order=${order.order_number}&orderId=SIM-MOCK-ID`);
          }
        })
        .catch(err => {
          sessionStorage.removeItem('hp_selected_plan');
          navigate(`/payment/callback?order=${order.order_number}&orderId=SIM-MOCK-ID`);
        });
      })
      .catch(err => {
        console.error("Order creation failed:", err);
        alert(lang === 'fr' ? 'Échec de création de la commande.' : 'Order creation failed.');
        setProcessing(false);
      });
  };

  if (!selectedPlan) return null;

  const selectedPlanName = lang === 'fr' ? (selectedPlan.name_fr || selectedPlan.name_en) : selectedPlan.name_en;
  const isOutOfStock = totalTickets < totalPlans;
  const isQuantityInvalid = isOutOfStock || quantity > totalTickets;

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Validation de la Commande' : 'Checkout'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Paiement' : 'Checkout'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container" style={{ maxWidth: '600px' }}>
          <div className="contact-form">
            <h2 style={{ marginBottom: '8px' }}>
              {lang === 'fr' ? 'Résumé de votre commande' : 'Order Summary'}
            </h2>
            <p style={{ color: 'var(--hp-gray-500)', marginBottom: '24px' }}>
              {selectedPlanName} — {selectedPlan.price} TND × {quantity} = <strong>{(parseFloat(selectedPlan.price) * quantity).toFixed(2)} TND</strong>
            </p>

            <form onSubmit={handlePurchase}>
              <div className="form-group" style={{ marginBottom: isQuantityInvalid ? '5px' : '20px' }}>
                <label>{lang === 'fr' ? 'Quantité' : 'Quantity'}</label>
                <input type="number" min="1" max="20" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} />
              </div>

              {isOutOfStock ? (
                <div style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '0.9rem', marginTop: '-10px', marginBottom: '20px' }}>
                  {lang === 'fr' ? 'Rupture de Stock' : 'Out Of Stock'}
                </div>
              ) : isQuantityInvalid && (
                <div style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '0.9rem', marginTop: '-10px', marginBottom: '20px' }}>
                  {lang === 'fr' ? 'Quantité indisponible' : 'Quantité indisponible'}
                </div>
              )}

              <div style={{ background: 'var(--hp-yellow-light)', padding: '16px', borderRadius: '12px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MIcon name="credit_card" size="1.5rem" />
                <div>
                  <strong>{lang === 'fr' ? 'Paiement Sécurisé via ClicToPay' : 'Secure Payment via ClicToPay'}</strong>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-gray-500)' }}>
                    {lang === 'fr' 
                      ? 'Votre paiement est traité en toute sécurité.' 
                      : 'Your payment is processed securely.'}
                  </div>
                </div>
              </div>

              <button type="submit" className="form-submit" style={{ width: '100%', cursor: isQuantityInvalid ? 'not-allowed' : 'pointer', background: isQuantityInvalid ? '#9ca3af' : '' }} disabled={processing || isQuantityInvalid}>
                {processing 
                  ? (lang === 'fr' ? 'Traitement en cours...' : 'Processing...') 
                  : (isOutOfStock 
                      ? (lang === 'fr' ? 'Rupture de Stock' : 'Out Of Stock')
                      : isQuantityInvalid
                        ? (lang === 'fr' ? 'Quantité indisponible' : 'Quantité indisponible')
                        : (lang === 'fr' 
                            ? `Payer ${(parseFloat(selectedPlan.price) * quantity).toFixed(2)} TND` 
                            : `Pay ${(parseFloat(selectedPlan.price) * quantity).toFixed(2)} TND`
                          )
                    )
                }
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: LOGIN
// ============================================
function LoginPage({ navigate, lang }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success(lang === 'fr' ? 'Bon retour !' : 'Welcome back!');
      const redirect = sessionStorage.getItem('redirect_after_login');
      if (redirect) {
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirect);
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.message || (lang === 'fr' ? 'Échec de connexion' : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Se connecter' : 'Log In'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Connexion' : 'Login'}</span>
        </div>
      </div>
      <section className="section">
        <div className="container" style={{ maxWidth: '480px' }}>
          <div className="contact-form">
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <img src={SITE_IMAGES.logo} alt="Logo" style={{ height: '60px', marginBottom: '16px' }} />
              <h2>{lang === 'fr' ? 'Bon retour !' : 'Welcome Back!'}</h2>
              <p style={{ color: 'var(--hp-gray-500)' }}>{lang === 'fr' ? 'Connectez-vous à votre compte Hannibal Park' : 'Sign in to your Hannibal Park account'}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required autoComplete="username" />
              </div>
              <div className="form-group">
                <label>{lang === 'fr' ? 'Mot de passe' : 'Password'}</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required autoComplete="current-password" />
              </div>
              <button type="submit" className="form-submit" style={{ width: '100%' }} disabled={loading}>
                {loading 
                  ? (lang === 'fr' ? 'Connexion...' : 'Signing in...') 
                  : (lang === 'fr' ? 'Se connecter' : 'Sign In')}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '0.9rem' }}>
              {lang === 'fr' ? "Vous n'avez pas de compte ?" : "Don't have an account?"}{' '}
              <a href="/register" onClick={e => { e.preventDefault(); navigate('/register'); }} style={{ color: 'var(--hp-blue)', fontWeight: 600 }}>
                {lang === 'fr' ? "S'inscrire" : 'Register'}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: REGISTER
// ============================================
function RegisterPage({ navigate, lang }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', confirm: '' });
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error(lang === 'fr' ? 'Les mots de passe ne correspondent pas !' : 'Passwords do not match!');
      return;
    }
    
    setLoading(true);
    try {
      await register({ ...form, subscribe_newsletter: subscribeNewsletter });
      // Auto-subscribe to newsletter if opted in
      if (subscribeNewsletter) {
        try { await api.subscribe(form.email); } catch (_) {}
      }
      toast.success(lang === 'fr' ? 'Compte créé avec succès !' : 'Account created successfully!');
      const redirect = sessionStorage.getItem('redirect_after_login');
      if (redirect) {
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirect);
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.message || (lang === 'fr' ? "Échec de l'inscription" : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? "S'inscrire" : 'Register'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>{lang === 'fr' ? "S'inscrire" : 'Register'}</span>
        </div>
      </div>
      <section className="section">
        <div className="container" style={{ maxWidth: '560px' }}>
          <div className="contact-form">
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <img src={SITE_IMAGES.logo} alt="Logo" style={{ height: '60px', marginBottom: '16px' }} />
              <h2>{lang === 'fr' ? 'Créer votre compte' : 'Create Your Account'}</h2>
              <p style={{ color: 'var(--hp-gray-500)' }}>{lang === 'fr' ? 'Rejoignez Hannibal Park et ne manquez jamais un moment de joie' : 'Join Hannibal Park and never miss a moment of joy'}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>{lang === 'fr' ? 'Prénom *' : 'First Name *'}</label>
                  <input type="text" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>{lang === 'fr' ? 'Nom *' : 'Last Name *'}</label>
                  <input type="text" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} required />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required autoComplete="email" />
              </div>
              <div className="form-group">
                <label>{lang === 'fr' ? 'Téléphone' : 'Phone'}</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>{lang === 'fr' ? 'Mot de passe *' : 'Password *'}</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={6} />
              </div>
              <div className="form-group">
                <label>{lang === 'fr' ? 'Confirmer le mot de passe *' : 'Confirm Password *'}</label>
                <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
              </div>
              {/* Newsletter opt-in */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '4px 0 20px', padding: '14px 16px', background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                <input
                  id="reg-newsletter"
                  type="checkbox"
                  checked={subscribeNewsletter}
                  onChange={e => setSubscribeNewsletter(e.target.checked)}
                  style={{ width: '16px', height: '16px', marginTop: '2px', accentColor: 'var(--hp-blue)', flexShrink: 0, cursor: 'pointer' }}
                />
                <label htmlFor="reg-newsletter" style={{ fontSize: '13.5px', color: '#1e40af', lineHeight: '1.5', cursor: 'pointer', fontWeight: 500 }}>
                  <MIcon name="email" size="1.1em" style={{ verticalAlign: 'middle', marginRight: '4.5px' }} /> {lang === 'fr'
                    ? 'J\'accepte de recevoir les newsletters de Hannibal Park (actualités, événements & offres exclusives)'
                    : 'I accept to receive Newsletters from Hannibal Park (news, events & exclusive offers)'}
                </label>
              </div>
              <button type="submit" className="form-submit" style={{ width: '100%' }} disabled={loading}>
                {loading ? '...' : (lang === 'fr' ? 'Créer un compte' : 'Create Account')}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '0.9rem' }}>
              {lang === 'fr' ? 'Vous avez déjà un compte ?' : 'Already have an account?'}{' '}
              <a href="/login" onClick={e => { e.preventDefault(); navigate('/login'); }} style={{ color: 'var(--hp-blue)', fontWeight: 600 }}>
                {lang === 'fr' ? 'Se connecter' : 'Sign In'}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: BUSINESS & PROFESSIONALS
// ============================================
function ProBusinessPage({ navigate, lang }) {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', partnerType: 'agency', message: '' });
  const [captcha] = useState(() => {
    const a = Math.floor(Math.random() * 9) + 1;
    const b = Math.floor(Math.random() * 9) + 1;
    return { a, b, answer: a + b };
  });
  const [captchaInput, setCaptchaInput] = useState('');
  const [loading, setLoading] = useState(false);

  const formRef = useRef(null);

  const scrollToForm = (e) => {
    e.preventDefault();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseInt(captchaInput) !== captcha.answer) {
      toast.error(lang === 'fr' ? 'Captcha invalide. Veuillez réessayer.' : 'Invalid captcha. Please try again.');
      return;
    }

    setLoading(true);
    const messageBody = `[B2B PARTNER REQUEST]
Company: ${form.company}
Phone: ${form.phone}
Partner Type: ${form.partnerType}

Message:
${form.message}`;

    try {
      await api.submitContact({
        name: form.name,
        email: form.email,
        message: messageBody,
        captcha_answer: captchaInput,
        captcha_expected: captcha.answer
      });
      toast.success(lang === 'fr' ? 'Votre demande de partenariat a été envoyée avec succès !' : 'Your partnership request has been submitted successfully!');
      setForm({ name: '', company: '', email: '', phone: '', partnerType: 'agency', message: '' });
      setCaptchaInput('');
    } catch (err) {
      toast.error(err.message || (lang === 'fr' ? 'Une erreur est survenue lors de l\'envoi.' : 'An error occurred during submission.'));
    } finally {
      setLoading(false);
    }
  };

  const b2bOpportunities = [
    {
      icon: 'flight',
      title: lang === 'fr' ? 'Agence de Voyage' : 'Travel Agency',
      items: lang === 'fr' ? [
        'Packs de Groupe sur Mesure : Forfaits exclusifs et personnalisables pour les visites de groupes.',
        'Réductions Spéciales : Tarifs préférentiels pour proposer des prix compétitifs.',
        'Accès VIP : Entrée rapide coupe-file et accueil privilégié pour vos groupes.'
      ] : [
        'Custom Group Packages: Exclusive, customizable packages for group visits.',
        'Special Discounts: Pre-negotiated rates to offer competitive pricing to clients.',
        'VIP Access: Fast-track entry and VIP group treatment for a premium guest experience.'
      ]
    },
    {
      icon: 'hotel',
      title: lang === 'fr' ? 'Hôtel & Hébergement' : 'Hotel & Lodging',
      items: lang === 'fr' ? [
        'Partenariats Hôteliers Exclusifs : Réductions spéciales sur l\'entrée pour vos résidents.',
        'Collaboration Promotionnelle : Visibilité conjointe sur les canaux digitaux et physiques.',
        'Packs Loisirs sur Mesure : Organisation d\'excursions d\'une journée pour animer les séjours.'
      ] : [
        'Exclusive Hotel Partnerships: Direct package offers and discounted entry for your guests.',
        'Promotional Collaboration: Dynamic visibility across digital and print marketing channels.',
        'Custom Entertainment Packages: Specialized leisure options like curated day trips.'
      ]
    },
    {
      icon: 'child_care',
      title: lang === 'fr' ? 'Jardin d\'Enfants' : 'Kindergarten',
      items: lang === 'fr' ? [
        'Aires de Jeux Dédiées : Espaces de jeux adaptés avec attractions sécurisées pour les petits.',
        'Ateliers & Événements Thématiques : Ateliers ludo-éducatifs autour de thèmes populaires.',
        'Cadre Sécurisé & Encadré : Staff formé et dédié pour veiller au bien-être de chaque enfant.'
      ] : [
        'Dedicated Play Areas: Fun spaces designed specifically for safety and early years play.',
        'Themed Events & Workshops: Instructive, hands-on activities led by character mascots.',
        'Safe & Supervised Environment: Constant care and monitoring by certified park hosts.'
      ]
    },
    {
      icon: 'school',
      title: lang === 'fr' ? 'Écoles & Lycées' : 'School & Education',
      items: lang === 'fr' ? [
        'Tarifs de Groupe Réduits : Tarifs scolaires étudiés et avantageux.',
        'Ateliers Pédagogiques : Thèmes d\'apprentissage variés (sciences amusantes, nature).',
        'Sorties de Fin d\'Année : Célébrations de fin de classe inoubliables avec activités collectives.'
      ] : [
        'Discounted Group Rates: Great value-focused admissions tailored for class field trips.',
        'Themed Educational Workshops: Learning opportunities on local culture and physics of rides.',
        'End-of-Year Celebrations: Festive events packed with team games, food, and endless fun.'
      ]
    },
    {
      icon: 'restaurant',
      title: lang === 'fr' ? 'Restaurant & Commerce' : 'Restaurant & Retail',
      items: lang === 'fr' ? [
        'Emplacements Stratégiques : Zones de fort passage garantissant un flux de clients continu.',
        'Espaces Modulables : Formats flexibles du simple kiosque à la terrasse complète.',
        'Assistance Administrative : Aide de nos équipes pour les permis et l\'installation.'
      ] : [
        'Prime Location & Foot Traffic: Top spots ensuring direct access to thousands of daily guests.',
        'Customizable Space Solutions: Modular layouts from standalone retail kiosks to full venues.',
        'Permits & Licensing Support: Guidance from our team to ensure rapid, compliant launches.'
      ]
    }
  ];

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Espace Professionnels' : 'Business & Professionals'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>{lang === 'fr' ? 'Accueil' : 'Home'}</a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Professionnels' : 'Business & Professionals'}</span>
        </div>
      </div>

      {/* Hero / Intro section */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '48px', alignItems: 'center' }}>
            <div>
              <span className="section-badge">{lang === 'fr' ? 'Devenez Partenaire' : 'Become Our Partner'}</span>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 800, margin: '16px 0', color: 'var(--hp-blue)', lineHeight: '1.2' }}>
                {lang === 'fr' ? 'Pourquoi collaborer avec nous ?' : 'Why partner with us?'}
              </h2>
              <p style={{ fontSize: '1.1rem', color: 'var(--hp-gray-600)', lineHeight: '1.8', marginBottom: '24px' }}>
                {lang === 'fr'
                  ? 'Avec des milliers de visiteurs chaque saison, nous amenons des clients diversifiés directement à votre porte. En une seule saison, nos partenaires ont obtenu un retour sur investissement exceptionnel dans toutes nos catégories, démontrant le succès de leurs projets.'
                  : 'With thousands of guests each season, we bring diverse customers directly to your doorstep. Within one season, our partners have experienced exceptional ROI across all our categories, showcasing the success of their businesses.'}
              </p>
              <button onClick={scrollToForm} className="btn-main" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <MIcon name="handshake" /> {lang === 'fr' ? 'Enregistrer mon intérêt' : 'Register Interest'}
              </button>
            </div>
            
            <div style={{ background: 'linear-gradient(135deg, #1e1b4b, #311042)', padding: '40px', borderRadius: '24px', color: 'white', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
              <MIcon name="shield" size="3rem" style={{ display: 'block', marginBottom: '16px', color: '#f59e0b' }} />
              <h3 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '12px', color: '#f59e0b' }}>
                {lang === 'fr' ? 'Risques limités' : 'Low risks'}
              </h3>
              <p style={{ lineHeight: '1.8', color: '#e2e8f0', fontSize: '1.05rem', marginBottom: '24px' }}>
                {lang === 'fr'
                  ? 'Avec un risque minimal et un engagement flexible, nous sommes l\'endroit idéal pour tester de nouveaux concepts, lancer de nouveaux produits ou accroître votre visibilité.'
                  : 'With low risk and minimal commitment, we are a perfect place to pilot new concepts, launch new products, or increase your brand exposure.'}
              </p>
              <div style={{ display: 'flex', gap: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                <div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f59e0b' }}>100%</div>
                  <div style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>{lang === 'fr' ? 'Flexibilité' : 'Flexibility'}</div>
                </div>
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '16px' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>ROI</div>
                  <div style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>{lang === 'fr' ? 'Assuré' : 'Proven Success'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section className="section section-light" style={{ padding: '80px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 60px' }}>
            <span className="section-badge">{lang === 'fr' ? 'Opportunités' : 'Opportunities We Offer'}</span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, margin: '16px 0', color: 'var(--hp-blue)' }}>
              {lang === 'fr'
                ? 'Hannibal Park est le lieu idéal pour développer votre activité'
                : 'Whether you\'re starting out or expanding your horizons, Hannibal Park is the place to be'}
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            {b2bOpportunities.map((opp, idx) => (
              <div key={idx} style={{ display: 'flex', background: 'white', borderRadius: '20px', padding: '32px', border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', transition: 'all 0.3s ease', gap: '24px' }} className="b2b-card">
                <div style={{ background: '#eff6ff', width: '80px', height: '80px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--hp-blue)' }}>
                  <MIcon name={opp.icon} size="2.8rem" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--hp-blue)', marginBottom: '16px' }}>{opp.title}</h3>
                  <ul style={{ paddingLeft: '0', listStyleType: 'none', display: 'flex', flexDirection: 'column', gap: '10px', margin: 0 }}>
                    {opp.items.map((item, i) => (
                      <li key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', color: 'var(--hp-gray-700)', lineHeight: '1.6' }}>
                        <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Counter section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, var(--hp-blue), #1e1b4b)', color: 'white', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-10%', left: '-10%', width: '30%', height: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)', pointerEvents: 'none' }}></div>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '40px', textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: '4.5rem', fontWeight: 900, color: '#f59e0b', lineHeight: '1' }}>100+</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, marginTop: '12px', color: '#e2e8f0' }}>
                {lang === 'fr' ? 'Opportunités Commerciales' : 'Business Opportunities'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '4.5rem', fontWeight: 900, color: '#10b981', lineHeight: '1' }}>2M+ TND</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, marginTop: '12px', color: '#e2e8f0' }}>
                {lang === 'fr' ? 'Transactions / An' : 'Transactions/Year'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '4.5rem', fontWeight: 900, color: '#38bdf8', lineHeight: '1' }}>100K+</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, marginTop: '12px', color: '#e2e8f0' }}>
                {lang === 'fr' ? 'Visiteurs Chaque Saison' : 'Of Guests Each Season'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section section-light" style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 60px' }}>
            <span className="section-badge">{lang === 'fr' ? 'Témoignages' : 'Testimonials'}</span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 800, margin: '16px 0', color: 'var(--hp-blue)' }}>
              {lang === 'fr' ? 'Les retours de nos clients' : 'See Our Clients Feedback'}
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {[
              {
                name: 'Jelena Rakovic',
                origin: lang === 'fr' ? 'Russie' : 'Russia',
                text: lang === 'fr'
                  ? '"Un endroit si joyeux et heureux. Idéal pour se divertir, des enfants aux adultes... recommande vivement !"'
                  : '"Such a happy and joyfully place. Great for entertainment, from kids to adults... there is a lot of different programs. Recommend."'
              },
              {
                name: 'Philipp Fleischer',
                origin: lang === 'fr' ? 'Allemagne' : 'Germany',
                text: lang === 'fr'
                  ? '"Nous avons payé environ 100 tnd et nous nous sommes tellement amusés, toute la journée à 5 personnes !"'
                  : '"We paid round about 100 tnd and Had so much fun, the whole day for 5 person."'
              },
              {
                name: 'Rislaine Mehi',
                origin: lang === 'fr' ? 'Royaume-Uni' : 'United Kingdom',
                text: lang === 'fr'
                  ? '"Excellent parc pour se promener ou s\'amuser, il y en a pour tous les goûts ! La gestion de budget avec carte est top !"'
                  : '"Excellent amusement park for walking or having fun, there is something for everyone! Operation with card for attractions is great."'
              }
            ].map((testi, i) => (
              <div key={i} style={{ background: 'white', borderRadius: '18px', padding: '32px', border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', justifyContent: 'between' }}>
                <div>
                  <div style={{ color: '#f59e0b', fontSize: '1.2rem', marginBottom: '16px' }}>★★★★★</div>
                  <p style={{ fontStyle: 'italic', color: 'var(--hp-gray-600)', lineHeight: '1.7', fontSize: '0.98rem', marginBottom: '24px' }}>{testi.text}</p>
                </div>
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'var(--hp-blue)', fontSize: '1.1rem' }}>
                    {testi.name[0]}
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700, color: 'var(--hp-blue)' }}>{testi.name}</h5>
                    <span style={{ fontSize: '0.8rem', color: 'var(--hp-gray-500)' }}>{testi.origin}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Interest Form */}
      <section className="section" ref={formRef} style={{ padding: '80px 0', scrollMarginTop: '80px' }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <div className="contact-form" style={{ boxShadow: '0 20px 50px rgba(0,0,0,0.08)', borderRadius: '24px', border: '1px solid #e2e8f0', padding: '48px' }}>
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
              <span className="section-badge">{lang === 'fr' ? 'Partenariat' : 'Partnership Form'}</span>
              <h2 style={{ fontSize: '2.2rem', fontWeight: 800, margin: '12px 0 8px', color: 'var(--hp-blue)' }}>
                {lang === 'fr' ? 'Déposez votre projet' : 'Register Your Interest'}
              </h2>
              <p style={{ color: 'var(--hp-gray-500)' }}>
                {lang === 'fr' ? 'Prenez contact avec notre équipe commerciale dès aujourd\'hui.' : 'Get in touch with our commercial operations team today.'}
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>{lang === 'fr' ? 'Nom du contact *' : 'Contact Name *'}</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="ex: Jean Dupont"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{lang === 'fr' ? 'Nom de l\'entreprise *' : 'Company Name *'}</label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                    placeholder="ex: Tunis Voyages"
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="ex: contact@entreprise.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{lang === 'fr' ? 'Téléphone *' : 'Phone *'}</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="ex: +216 22 222 222"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{lang === 'fr' ? 'Secteur d\'activité *' : 'Business Sector *'}</label>
                <select
                  value={form.partnerType}
                  onChange={e => setForm(f => ({ ...f, partnerType: e.target.value }))}
                  style={{ width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', background: 'white' }}
                >
                  <option value="agency">{lang === 'fr' ? 'Agence de Voyage' : 'Travel Agency'}</option>
                  <option value="hotel">{lang === 'fr' ? 'Hôtel' : 'Hotel'}</option>
                  <option value="kindergarten">{lang === 'fr' ? 'Jardin d\'Enfants' : 'Kindergarten'}</option>
                  <option value="school">{lang === 'fr' ? 'Établissement Scolaire' : 'School'}</option>
                  <option value="restaurant">{lang === 'fr' ? 'Restauration / Alimentation' : 'Restaurant & Food'}</option>
                  <option value="retail">{lang === 'fr' ? 'Vente au détail / Boutique' : 'Retail / Vendor'}</option>
                  <option value="sponsor">{lang === 'fr' ? 'Sponsor / Publicité' : 'Sponsorship & Ads'}</option>
                  <option value="other">{lang === 'fr' ? 'Autre' : 'Other'}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{lang === 'fr' ? 'Décrivez votre proposition / demande *' : 'Describe your proposal / request *'}</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder={lang === 'fr' ? 'Dites-nous en plus sur votre projet de partenariat...' : 'Tell us more about your partnership idea...'}
                  rows="6"
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label>Captcha : {captcha.a} + {captcha.b} = ? *</label>
                <input
                  type="number"
                  value={captchaInput}
                  onChange={e => setCaptchaInput(e.target.value)}
                  placeholder="?"
                  required
                />
              </div>

              <button type="submit" className="form-submit" style={{ width: '100%' }} disabled={loading}>
                {loading ? '...' : (lang === 'fr' ? 'Soumettre ma demande' : 'Submit Partnership Request')}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: CATEGORY (attractions-for-adventure / childs / parents)
// ============================================
const THRILL_CONFIG = {
  low:     { label: 'Low Thrill',     color: '#22c55e', icon: 'sentiment_satisfied' },
  medium:  { label: 'Medium Thrill',  color: '#f59e0b', icon: 'sentiment_very_satisfied' },
  high:    { label: 'High Thrill',    color: '#f97316', icon: 'mood_bad' },
  extreme: { label: 'Extreme Thrill', color: '#ef4444', icon: 'whatshot' },
};

const CATEGORY_HERO = {
  'attractions-for-adventure': { icon: 'attractions', gradient: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c4a6e 100%)', accent: '#00d4ff' },
  'attractions-for-childs':    { icon: 'child_care', gradient: 'linear-gradient(135deg, #14532d 0%, #166534 50%, #15803d 100%)', accent: '#86efac' },
  'attractions-for-parents':   { icon: 'diversity_1', gradient: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #7c3aed 100%)', accent: '#c4b5fd' },
};

function CategoryPage({ slug, navigate, lang }) {
  const [category, setCategory] = useState(null);
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    import('./utils/api').then(({ default: api }) => {
      api.getCategory(slug)
        .then(data => {
          setCategory(data.category);
          setAttractions(data.category?.attractions || []);
        })
        .catch(err => setError(err.message || 'Failed to load attractions'))
        .finally(() => setLoading(false));
    });
  }, [slug]);

  const hero = CATEGORY_HERO[slug] || { icon: 'attractions', gradient: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', accent: '#94a3b8' };

  return (
    <>
      {/* Hero Banner */}
      <div className="cat-hero" style={{ background: hero.gradient }}>
        <div className="cat-hero-inner">
          <div className="breadcrumb" style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px', justifyContent: 'center' }}>
            <a href="/" style={{ color: 'rgba(255,255,255,0.6)' }} onClick={e => { e.preventDefault(); navigate('/'); }}>
              {lang === 'fr' ? 'Accueil' : 'Home'}
            </a>
            <span style={{ margin: '0 8px' }}>/</span>
            <a href="/attractions" style={{ color: 'rgba(255,255,255,0.6)' }} onClick={e => { e.preventDefault(); navigate('/attractions'); }}>
              {lang === 'fr' ? 'Attractions' : 'Attractions'}
            </a>
            <span style={{ margin: '0 8px' }}>/</span>
            <span style={{ color: 'white' }}>
              {category ? (lang === 'fr' ? (category.name_fr || category.name_en) : category.name_en) : slug}
            </span>
          </div>
          {loading ? (
            <h1 className="cat-hero-title">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</h1>
          ) : (
            <>
              <h1 className="cat-hero-title">
                {lang === 'fr' ? (category?.name_fr || category?.name_en || slug) : (category?.name_en || slug)}
              </h1>
              <p className="cat-hero-desc">
                {lang === 'fr' ? (category?.description_fr || category?.description_en || '') : (category?.description_en || '')}
              </p>
              <div className="cat-hero-count" style={{ color: hero.accent }}>
                {attractions.length} {attractions.length === 1 ? (lang === 'fr' ? 'Attraction' : 'Attraction') : (lang === 'fr' ? 'Attractions' : 'Attractions')}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Attractions Grid */}
      <section className="section">
        <div className="container">
          {loading && (
            <div className="cat-loading">
              {[1,2,3,4,5,6].map(i => <div key={i} className="cat-skeleton" />)}
            </div>
          )}

          {error && (
            <div className="cat-error">
              <div className="cat-error-icon"><MIcon name="warning" /></div>
              <h3>Oops! Something went wrong</h3>
              <p>{error}</p>
              <button className="btn-main" onClick={() => window.location.reload()}>Try Again</button>
            </div>
          )}

          {!loading && !error && attractions.length === 0 && (
            <div className="cat-empty">
              <div style={{ marginBottom: '16px' }}><MIcon name="attractions" size="4rem" /></div>
              <h3>No attractions yet</h3>
              <p style={{ color: 'var(--hp-gray-500)' }}>Check back soon — new rides are coming!</p>
            </div>
          )}

          {!loading && !error && attractions.length > 0 && (
            <div className="attractions-grid">
              {attractions.map((attraction, i) => {
                const thrill = THRILL_CONFIG[attraction.thrill_level] || THRILL_CONFIG.medium;
                const imgSrc = attraction.image
                  ? `${BASE_SERVER_URL}/uploads/attractions/${attraction.image.split('/').pop()}`
                  : `https://www.hannibalpark.com/assets/img/categories/category_68dd041069458.jpg`;
                return (
                  <AnimatedSection key={attraction.id || i}>
                    <article className="attraction-card">
                      {/* Image */}
                      <div className="ac-img-wrap">
                        <img src={imgSrc} alt={attraction.name_en} loading="lazy" />
                        {/* Thrill Badge */}
                        <div className="ac-thrill" style={{ background: thrill.color, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <MIcon name={thrill.icon} size="1.1em" /> {thrill.label}
                        </div>
                      </div>

                      {/* Body */}
                      <div className="ac-body">
                        <h3 className="ac-name">{attraction.name_en}</h3>
                        {attraction.short_description_en && (
                          <p className="ac-desc">{attraction.short_description_en}</p>
                        )}

                        {/* Meta Info */}
                        <div className="ac-meta">
                          {attraction.min_height && (
                            <div className="ac-meta-item">
                              <MIcon name="straighten" className="ac-meta-icon" size="1.1em" />
                              <span>{lang === 'fr' ? `Min ${attraction.min_height} cm` : `Min ${attraction.min_height} cm`}</span>
                            </div>
                          )}
                          {attraction.min_age && (
                            <div className="ac-meta-item">
                              <MIcon name="child_care" className="ac-meta-icon" size="1.1em" />
                              <span>{lang === 'fr' ? `Âge ${attraction.min_age}+` : `Age ${attraction.min_age}+`}</span>
                            </div>
                          )}
                          {attraction.duration_minutes && (
                            <div className="ac-meta-item">
                              <MIcon name="timer" className="ac-meta-icon" size="1.1em" />
                              <span>{attraction.duration_minutes} min</span>
                            </div>
                          )}
                          {attraction.capacity && (
                            <div className="ac-meta-item">
                              <MIcon name="group" className="ac-meta-icon" size="1.1em" />
                              <span>{attraction.capacity} {lang === 'fr' ? 'places' : 'riders'}</span>
                            </div>
                          )}
                          {attraction.is_accessible === 1 && (
                            <div className="ac-meta-item ac-accessible">
                              <MIcon name="accessible" className="ac-meta-icon" size="1.1em" />
                              <span>{lang === 'fr' ? 'Accessible' : 'Accessible'}</span>
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <button
                          className="ac-cta"
                          style={{ border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                          onClick={() => navigate(`/attraction/${attraction.slug}`)}
                        >
                          <MIcon name="search" size="1.1em" />
                          {lang === 'fr' ? 'Découvrir...' : 'Discover Me...'}
                        </button>
                      </div>
                    </article>
                  </AnimatedSection>
                );
              })}
            </div>
          )}

          {/* Back CTA */}
          {!loading && (
            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <button
                className="btn-sec"
                onClick={() => navigate('/attractions')}
                style={{ cursor: 'pointer' }}
              >
                ← All Categories
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: ATTRACTION DETAILS PAGE (FULL PAGE)
// ============================================
function AttractionDetailPage({ slug, navigate, lang }) {
  const [attraction, setAttraction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLightboxImage, setActiveLightboxImage] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    import('./utils/api').then(({ default: api }) => {
      api.getAttraction(slug)
        .then(data => {
          setAttraction(data.attraction);
        })
        .catch(err => {
          setError(err.message || 'Failed to load attraction');
        })
        .finally(() => setLoading(false));
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="container" style={{ padding: '120px 24px', textAlign: 'center' }}>
        <div className="cat-loading" style={{ margin: '0 auto', maxWidth: '900px' }}>
          <div className="cat-skeleton" style={{ height: '320px', borderRadius: '24px', marginBottom: '24px' }} />
          <div className="cat-skeleton" style={{ height: '80px', borderRadius: '16px', marginBottom: '16px' }} />
          <div className="cat-skeleton" style={{ height: '200px', borderRadius: '16px' }} />
        </div>
      </div>
    );
  }

  if (error || !attraction) {
    return (
      <div className="container" style={{ padding: '120px 24px', textAlign: 'center' }}>
        <div className="cat-error">
          <div className="cat-error-icon"><MIcon name="warning" /></div>
          <h3>{lang === 'fr' ? 'Jeu introuvable' : 'Attraction Not Found'}</h3>
          <p>{error || (lang === 'fr' ? "Le jeu demandé n'a pas pu être trouvé ou est inactif." : 'The requested attraction could not be found or is inactive.')}</p>
          <button className="btn-main" onClick={() => navigate('/attractions')}>
            {lang === 'fr' ? 'Toutes les attractions' : 'Browse Attractions'}
          </button>
        </div>
      </div>
    );
  }

  // Parse media (images/videos)
  let galleryImages = [];
  let galleryVideos = [];
  if (attraction.gallery) {
    try {
      const parsed = JSON.parse(attraction.gallery);
      galleryImages = parsed.images || [];
      galleryVideos = parsed.videos || [];
    } catch (e) {
      console.error("Failed to parse gallery JSON", e);
    }
  }

  const thrill = THRILL_CONFIG[attraction.thrill_level] || THRILL_CONFIG.medium;
  const imgSrc = attraction.image
    ? `${BASE_SERVER_URL}/uploads/attractions/${attraction.image.split('/').pop()}`
    : `https://www.hannibalpark.com/assets/img/categories/category_68dd041069458.jpg`;

  return (
    <>
      {/* High-Impact Hero Banner */}
      <div className="cat-hero" style={{ 
        backgroundColor: '#0f172a',
        paddingTop: '130px',
        paddingBottom: '50px'
      }}>
        <div className="cat-hero-inner" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div className="breadcrumb" style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
            <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
              {lang === 'fr' ? 'Accueil' : 'Home'}
            </a>
            <span>/</span>
            <a href="/attractions" onClick={e => { e.preventDefault(); navigate('/attractions'); }}>
              {lang === 'fr' ? 'Attractions' : 'Attractions'}
            </a>
            <span>/</span>
            {attraction.category_slug ? (
              <a href={`/category/${attraction.category_slug}`} onClick={e => { e.preventDefault(); navigate(`/category/${attraction.category_slug}`); }}>
                {lang === 'fr' ? (attraction.category_name_fr || attraction.category_name_en) : attraction.category_name_en}
              </a>
            ) : (
              <a href="/attractions" onClick={e => { e.preventDefault(); navigate('/attractions'); }}>
                {lang === 'fr' ? 'Attractions' : 'Attractions'}
              </a>
            )}
            <span>/</span>
            <span style={{ color: '#ffffff', fontWeight: '600' }}>
              {lang === 'fr' ? attraction.name_fr : attraction.name_en}
            </span>
          </div>
          
          <div className="ad-modal-thrill" style={{ background: thrill.color, margin: '0 auto 16px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <MIcon name={thrill.icon} size="1.1em" /> {lang === 'fr' ? (attraction.thrill_level === 'low' ? 'Sensation Douce' : attraction.thrill_level === 'medium' ? 'Sensation Modérée' : attraction.thrill_level === 'high' ? 'Forte Sensation' : 'Sensation Extrême') : thrill.label}
          </div>
          <h1 className="cat-hero-title" style={{ fontSize: '3rem', fontFamily: 'Fredoka, sans-serif' }}>
            {lang === 'fr' ? attraction.name_fr : attraction.name_en}
          </h1>
          <p className="cat-hero-desc" style={{ fontStyle: 'italic', opacity: 0.8, fontSize: '1.2rem' }}>
            {lang === 'fr' ? attraction.name_en : attraction.name_fr}
          </p>
        </div>
      </div>

      {/* Main Attraction content page */}
      <section className="section" style={{ background: '#f8fafc', padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          
          {/* Card containing all details */}
          <div className="form-section" style={{ background: '#ffffff', padding: '40px', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', marginBottom: '40px' }}>
            
            {/* Grid layout: Cover image on left, Short Description & Specs on right */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', marginBottom: '40px' }}>
              {/* Left Column: Cover Image */}
              <div>
                <img 
                  src={imgSrc} 
                  alt={lang === 'fr' ? attraction.name_fr : attraction.name_en} 
                  style={{ width: '100%', height: '380px', objectFit: 'cover', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.08)' }} 
                />
              </div>

              {/* Right Column: Short Description & Specifications */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <h6 style={{ fontSize: '1rem', color: '#e41e31', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '800', marginBottom: '12px' }}>
                  {lang === 'fr' ? (attraction.category_name_fr || attraction.category_name_en) : attraction.category_name_en}
                </h6>

                <p style={{ fontSize: '1.15rem', lineHeight: '1.7', color: '#475569', marginBottom: '24px', fontWeight: '500' }}>
                  {lang === 'fr' 
                    ? (attraction.short_description_fr || attraction.short_description_en || '') 
                    : (attraction.short_description_en || '')}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', borderTop: '2px solid #f1f5f9', padding: '20px 0 0' }}>
                  {attraction.min_age && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.05rem', fontWeight: '700', color: '#1e293b' }}>
                      <img src="/age-icon.png" alt="Age" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                      <span>{lang === 'fr' ? `Âge : de ${attraction.min_age} à 27 ans` : `Age: ${attraction.min_age} to 27 years`}</span>
                    </div>
                  )}
                  {attraction.min_height && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.05rem', fontWeight: '700', color: '#1e293b' }}>
                      <img src="/height-icon.png" alt="Height" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                      <span>{lang === 'fr' ? `Taille : de ${attraction.min_height} à ${attraction.max_height || 170} cm` : `Height: ${attraction.min_height} to ${attraction.max_height || 170} cm`}</span>
                    </div>
                  )}
                  {attraction.capacity && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.05rem', fontWeight: '700', color: '#1e293b' }}>
                      <img src="/capacity-icon.png" alt="Capacity" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                      <span>{lang === 'fr' ? `Capacité : ${attraction.capacity} personnes` : `Capacity: ${attraction.capacity} persons`}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.05rem', fontWeight: '700', color: '#1e293b' }}>
                    <img src="/tickets-icon.png" alt="Points" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                    <span>{lang === 'fr' ? `${attraction.points_required || 0} POINTS` : `${attraction.points_required || 0} POINTS`}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Gallery Media Section */}
            {galleryImages.length > 0 && (
              <div className="ad-media-section" style={{ marginBottom: '40px', borderTop: '2px solid #f1f5f9', paddingTop: '32px' }}>
                <h3 className="ad-media-section-title" style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '20px', fontFamily: 'Fredoka, sans-serif' }}>
                  {lang === 'fr' ? `Galerie de ${attraction.name_fr}` : `${attraction.name_en} Gallery`}
                </h3>
                <div className="ad-gallery-scroll">
                  {galleryImages.map((img, idx) => (
                    <img 
                      key={idx}
                      src={`${BASE_SERVER_URL}/uploads/attractions/${img}`}
                      alt={`${attraction.name_en} gallery ${idx + 1}`}
                      className="ad-gallery-img"
                      style={{ cursor: 'pointer', transition: 'transform 0.3s ease' }}
                      onClick={() => setActiveLightboxImage(img)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Videos Section */}
            {galleryVideos.length > 0 && (
              <div className="ad-media-section" style={{ marginBottom: '40px' }}>
                <h3 className="ad-media-section-title" style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '20px', fontFamily: 'Fredoka, sans-serif' }}>
                  {lang === 'fr' ? 'Vidéos de présentation' : 'Video Previews'}
                </h3>
                {galleryVideos.map((vid, idx) => (
                  <div key={idx} className="ad-video-container" style={{ marginTop: '12px' }}>
                    <video 
                      src={`${BASE_SERVER_URL}/uploads/attractions/${vid}`} 
                      controls 
                      className="ad-video"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Full Description Section replacing Park Rules */}
            <div style={{ marginTop: '40px', padding: '32px', background: '#f8fafc', borderLeft: '4px solid #e41e31', borderRadius: '12px' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e293b', marginBottom: '16px', fontFamily: 'Fredoka, sans-serif', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MIcon name="menu_book" size="1.2em" /> {lang === 'fr' ? 'Description Complète' : 'Full Description'}
              </h3>
              <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#334155', whiteSpace: 'pre-line' }}>
                {lang === 'fr' 
                  ? (attraction.description_fr || attraction.description_en || 'Pas de description complète disponible.') 
                  : (attraction.description_en || 'No full description available.')}
              </div>
            </div>

          </div>

          {/* Bottom Back Button */}
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <button 
              className="btn-sec"
              onClick={() => navigate(attraction.category_slug ? `/category/${attraction.category_slug}` : '/attractions')}
              style={{ cursor: 'pointer', background: '#ffffff', border: '1px solid #cbd5e1', color: '#1e293b', padding: '12px 24px', borderRadius: '50px', fontWeight: '700' }}
            >
              {lang === 'fr' ? '← Retour aux attractions' : '← Back to Attractions'}
            </button>
          </div>

        </div>
      </section>

      {/* Lightbox Modal for Gallery Images */}
      {activeLightboxImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
          onClick={() => setActiveLightboxImage(null)}
        >
          <button 
            style={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: 'white',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onClick={() => setActiveLightboxImage(null)}
          >
            ✕
          </button>
          <img 
            src={`${BASE_SERVER_URL}/uploads/attractions/${activeLightboxImage}`} 
            alt="Expanded view" 
            style={{
              maxWidth: '90%',
              maxHeight: '85%',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              cursor: 'default'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

// ============================================
// PAGE: PROFILE
// ============================================
function ProfilePage({ navigate, lang }) {
  const { user, logout, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    api.getMyTickets()
      .then(data => {
        setTickets(data.tickets || []);
      })
      .catch(err => {
        console.error("Failed to load tickets", err);
        if (err.message === 'Unauthorized' || err.message.includes('401')) {
           logout();
           navigate('/login');
        }
      })
      .finally(() => setLoading(false));
  }, [user, navigate, logout, authLoading]);

  if (authLoading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!user) return null;

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Mon Profil' : 'My Profile'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Profil' : 'Profile'}</span>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="profile-grid">
            
            {/* Left Col: User Details */}
            <div className="contact-form" style={{ alignSelf: 'start' }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--hp-blue)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', margin: '0 auto 16px', fontWeight: 'bold' }}>
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <h3>{user.first_name} {user.last_name}</h3>
                <p style={{ color: 'var(--hp-gray-500)' }}>{user.email}</p>
              </div>
              <hr style={{ margin: '24px 0', borderColor: 'var(--hp-gray-200)' }} />
              <button 
                onClick={() => navigate('/check-balance')} 
                className="btn-main" 
                style={{ width: '100%', marginBottom: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 24px', fontSize: '0.95rem' }}
              >
                <MIcon name="account_balance_wallet" />
                {lang === 'fr' ? 'Vérifier mon solde' : 'Check my balance'}
              </button>
              <button 
                onClick={() => { logout(); navigate('/'); }} 
                className="btn-sec" 
                style={{ width: '100%', borderColor: 'red', color: 'red' }}
              >
                {lang === 'fr' ? 'Se déconnecter' : 'Logout'}
              </button>
            </div>

            {/* Right Col: Orders/Tickets */}
            <div className="contact-form">
              <h2 style={{ marginBottom: '24px' }}>
                {lang === 'fr' ? 'Mes Tickets Commandés' : 'My Ordered Tickets'}
              </h2>
              
              {loading ? (
                <p>{lang === 'fr' ? 'Chargement...' : 'Loading...'}</p>
              ) : tickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', background: 'var(--hp-gray-100)', borderRadius: '12px' }}>
                  <p style={{ color: 'var(--hp-gray-500)', marginBottom: '16px' }}>
                    {lang === 'fr' ? 'Vous n\'avez pas encore commandé de tickets.' : 'You have not ordered any tickets yet.'}
                  </p>
                  <button onClick={() => navigate('/buy-tickets')} className="btn-primary">
                    {lang === 'fr' ? 'Acheter des Tickets' : 'Buy Tickets'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {tickets.map((t, i) => (
                    <div key={i} style={{ border: '1px solid var(--hp-gray-200)', borderRadius: '12px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ marginBottom: '4px' }}>
                          {lang === 'fr' ? 'Ticket' : 'Ticket'} {t.ean13 ? `EAN: ${t.ean13}` : `#${t.ticket_code}`}
                        </h4>
                        <div style={{ color: 'var(--hp-gray-500)', fontSize: '0.9rem' }}>
                          {lang === 'fr' ? t.name_fr : t.name_en} - {lang === 'fr' ? 'Prix Carte:' : 'Ticket Price:'} {t.price} TND
                        </div>
                        <div style={{ color: 'var(--hp-green)', fontWeight: 'bold', fontSize: '0.9rem', marginTop: '4px' }}>
                          {t.status === 'vendu' ? (lang === 'fr' ? 'Vendu / Actif' : 'Sold / Active') : t.status}
                        </div>
                        {t.pdf_url && (
                          <button 
                            onClick={async () => {
                              try {
                                const blob = await api.downloadTicket(t.id);
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `card_${t.ean13 || t.ticket_code || t.id}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                console.error("Secure download failed:", err);
                                toast.error(lang === 'fr' ? 'Échec du téléchargement sécurisé' : 'Secure download failed');
                              }
                            }}
                            className="link-btn"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.85rem', color: 'var(--hp-blue)', textDecoration: 'underline', fontWeight: 'bold' }}
                          >
                            <MIcon name="picture_as_pdf" size="1.1em" />
                            {lang === 'fr' ? 'Télécharger la carte PDF' : 'Download PDF Card'}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                        {t.valid_until}
                        <div style={{ fontSize: '0.8rem', color: 'var(--hp-gray-500)', fontWeight: 'normal' }}>
                          {lang === 'fr' ? 'Expire le' : 'Expires on'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: CHECK BALANCE
// ============================================
function CheckBalancePage({ navigate, lang }) {
  const { user, logout, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [barcode, setBarcode] = useState('');
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [balance, setBalance] = useState(null);
  const [checkedCard, setCheckedCard] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    api.getMyTickets()
      .then(data => {
        setTickets(data.tickets || []);
      })
      .catch(err => {
        console.error("Failed to load tickets", err);
        if (err.message === 'Unauthorized' || err.message.includes('401')) {
           logout();
           navigate('/login');
        }
      })
      .finally(() => setLoadingTickets(false));
  }, [user, navigate, logout, authLoading]);

  useEffect(() => {
    let scanner = null;
    if (isScanning) {
      setBalance(null);
      setError(null);
      
      const timer = setTimeout(() => {
        try {
          scanner = new Html5QrcodeScanner(
            "barcode-reader",
            { 
              fps: 10, 
              qrbox: { width: 250, height: 150 },
              aspectRatio: 1.777778
            },
            false
          );
          scanner.render(
            (decodedText) => {
              setBarcode(decodedText);
              setIsScanning(false);
              scanner.clear().catch(err => console.error("Error clearing scanner:", err));
              handleCheckBalance(decodedText);
            },
            (err) => {
              // Silent scanning errors
            }
          );
        } catch (e) {
          console.error("Failed to start scanner:", e);
          setError(lang === 'fr' ? "Impossible d'accéder à la caméra." : "Unable to access camera.");
          setIsScanning(false);
        }
      }, 300);

      return () => {
        clearTimeout(timer);
        if (scanner) {
          scanner.clear().catch(err => console.error("Error clearing scanner on unmount:", err));
        }
      };
    }
  }, [isScanning, lang]);

  const handleCheckBalance = async (codeToCheck) => {
    const code = codeToCheck || barcode;
    if (!code || code.trim() === '') {
      toast.error(lang === 'fr' ? 'Veuillez entrer ou scanner un code.' : 'Please enter or scan a code.');
      return;
    }
    setLoadingBalance(true);
    setBalance(null);
    setError(null);
    setCheckedCard(code);
    try {
      const res = await api.getCardBalance(code.trim());
      if (res && res['Solde restant'] !== undefined) {
        setBalance(res['Solde restant']);
        toast.success(lang === 'fr' ? 'Solde récupéré !' : 'Balance retrieved!');
        api.getMyTickets().then(data => setTickets(data.tickets || [])).catch(() => {});
      } else {
        setError(lang === 'fr' ? 'Réponse invalide de l\'API' : 'Invalid response from API');
      }
    } catch (err) {
      console.error("Failed to get balance:", err);
      setError(lang === 'fr' ? 'Impossible de récupérer le solde. Vérifiez le code carte.' : 'Failed to retrieve balance. Check the card code.');
    } finally {
      setLoadingBalance(false);
    }
  };

  if (authLoading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!user) return null;

  return (
    <>
      <div className="page-header">
        <h1>{lang === 'fr' ? 'Solde de vos cartes' : 'Check Card Balance'}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>
            {lang === 'fr' ? 'Accueil' : 'Home'}
          </a>
          <span>/</span>
          <a href="/profile" onClick={e => { e.preventDefault(); navigate('/profile'); }}>
            {lang === 'fr' ? 'Profil' : 'Profile'}
          </a>
          <span>/</span>
          <span>{lang === 'fr' ? 'Solde Carte' : 'Card Balance'}</span>
        </div>
      </div>

      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }}>
            
            <div className="contact-form" style={{ width: '100%' }}>
              <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MIcon name="search" size="1.2em" />
                {lang === 'fr' ? 'Vérifier une carte par Code / Scan' : 'Check Card by Code / Scan'}
              </h3>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <input 
                  type="text" 
                  placeholder={lang === 'fr' ? "Entrez le code EAN13 (ex: 7888640299649)" : "Enter EAN13 Code (e.g. 7888640299649)"}
                  value={barcode}
                  onChange={e => setBarcode(e.target.value)}
                  style={{ flex: 1, minWidth: '250px', padding: '14px', borderRadius: '12px', border: '1px solid var(--hp-gray-300)', fontFamily: 'var(--font-body)', fontSize: '1rem' }}
                />
                
                <button 
                  onClick={() => handleCheckBalance()} 
                  className="btn-main" 
                  disabled={loadingBalance}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 28px', fontSize: '1rem' }}
                >
                  {loadingBalance ? '...' : (lang === 'fr' ? 'Vérifier' : 'Check')}
                </button>

                <button 
                  onClick={() => setIsScanning(!isScanning)} 
                  className="btn-sec"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 24px', fontSize: '1rem', borderColor: 'var(--hp-blue)', color: 'var(--hp-blue)' }}
                >
                  <MIcon name={isScanning ? "videocam_off" : "qr_code_scanner"} />
                  {isScanning ? (lang === 'fr' ? 'Arrêter' : 'Stop Scan') : (lang === 'fr' ? 'Scanner' : 'Scan')}
                </button>
              </div>

              {isScanning && (
                <div style={{ background: '#000', borderRadius: '16px', padding: '16px', marginBottom: '24px', boxShadow: 'inset 0 0 20px rgba(255,255,255,0.2)' }}>
                  <p style={{ color: '#fff', textAlign: 'center', marginBottom: '12px', fontSize: '0.9rem' }}>
                    {lang === 'fr' ? "Présentez le code-barres devant la caméra" : "Place the barcode in front of your camera"}
                  </p>
                  <div id="barcode-reader" style={{ width: '100%', maxWidth: '500px', margin: '0 auto', overflow: 'hidden', borderRadius: '12px' }}></div>
                </div>
              )}

              {(balance !== null || error || loadingBalance) && (
                <div style={{ 
                  background: error ? '#fef2f2' : 'var(--hp-blue-light)', 
                  border: `2px solid ${error ? '#fca5a5' : 'var(--hp-blue)'}`, 
                  borderRadius: '16px', 
                  padding: '24px', 
                  textAlign: 'center', 
                  marginBottom: '24px',
                  animation: 'fadeIn 0.3s ease-in-out'
                }}>
                  {loadingBalance ? (
                    <div>
                      <MIcon name="hourglass_empty" size="3rem" style={{ color: 'var(--hp-blue)', animation: 'spin 1.5s linear infinite' }} />
                      <h4 style={{ marginTop: '12px' }}>{lang === 'fr' ? 'Interrogation du solde...' : 'Querying balance...'}</h4>
                    </div>
                  ) : error ? (
                    <div>
                      <MIcon name="error" size="3rem" style={{ color: '#ef4444' }} />
                      <h4 style={{ marginTop: '12px', color: '#b91c1c' }}>{lang === 'fr' ? 'Erreur' : 'Error'}</h4>
                      <p style={{ color: '#7f1d1d', marginTop: '6px' }}>{error}</p>
                    </div>
                  ) : (
                    <div>
                      <MIcon name="check_circle" size="3rem" style={{ color: 'var(--hp-green)' }} />
                      <h4 style={{ marginTop: '12px', color: 'var(--hp-dark)', fontSize: '1.2rem' }}>
                        {lang === 'fr' ? 'Solde Restant Récupéré' : 'Remaining Balance Retrieved'}
                      </h4>
                      <p style={{ color: 'var(--hp-gray-500)', fontSize: '0.9rem', marginTop: '4px' }}>
                        {lang === 'fr' ? 'Code Carte :' : 'Card Code:'} <strong>{checkedCard}</strong>
                      </p>
                      <div style={{ fontSize: '3rem', fontWeight: 'bold', color: 'var(--hp-purple)', margin: '16px 0', fontFamily: 'var(--font-display)' }}>
                        {balance} <span style={{ fontSize: '1.5rem' }}>{lang === 'fr' ? 'Points' : 'Points'}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="contact-form">
              <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MIcon name="style" size="1.2em" />
                {lang === 'fr' ? 'Vos cartes déjà achetées' : 'Your Purchased Cards'}
              </h3>

              {loadingTickets ? (
                <p>{lang === 'fr' ? 'Chargement de vos cartes...' : 'Loading your cards...'}</p>
              ) : tickets.length === 0 ? (
                <p style={{ color: 'var(--hp-gray-500)', textAlign: 'center', padding: '20px' }}>
                  {lang === 'fr' ? "Aucune carte trouvée dans votre historique." : "No cards found in your history."}
                </p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {tickets.filter(t => t.ean13).map((t, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        border: '2px solid var(--hp-gray-200)', 
                        borderRadius: '16px', 
                        padding: '20px', 
                        background: '#fff',
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'space-between',
                        boxShadow: 'var(--hp-shadow-sm)',
                        transition: 'transform 0.2s, border-color 0.2s',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setBarcode(t.ean13);
                        handleCheckBalance(t.ean13);
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.borderColor = 'var(--hp-blue)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = 'var(--hp-gray-200)';
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '0.8rem', background: 'var(--hp-blue-light)', color: 'var(--hp-blue-dark)', padding: '4px 8px', borderRadius: '50px', fontWeight: 'bold' }}>
                            {lang === 'fr' ? 'Active' : 'Active'}
                          </span>
                          <MIcon name="payment" size="1.2em" style={{ color: 'var(--hp-gray-400)' }} />
                        </div>
                        <h4 style={{ marginBottom: '6px', fontSize: '1.05rem' }}>
                          {lang === 'fr' ? t.name_fr : t.name_en}
                        </h4>
                        <p style={{ color: 'var(--hp-gray-500)', fontSize: '0.85rem', marginBottom: '12px' }}>
                          EAN: {t.ean13}
                        </p>
                      </div>
                      <div style={{ borderTop: '1px solid var(--hp-gray-100)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--hp-gray-500)' }}>
                          {lang === 'fr' ? 'Dernier solde :' : 'Last balance:'} <strong>{t.points_remaining} pts</strong>
                        </span>
                        <span style={{ color: 'var(--hp-blue)', fontSize: '0.85rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {lang === 'fr' ? 'Interroger' : 'Query'} →
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button 
                className="btn-sec"
                onClick={() => navigate('/profile')}
                style={{ cursor: 'pointer', background: '#ffffff', border: '1px solid #cbd5e1', color: '#1e293b', padding: '12px 24px', borderRadius: '50px', fontWeight: '700' }}
              >
                {lang === 'fr' ? '← Retour au profil' : '← Back to Profile'}
              </button>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// PAGE: PAYMENT CALLBACK
// ============================================
function PaymentCallbackPage({ navigate, lang }) {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const search = window.location.search;
    api.handlePaymentCallback(search)
      .then(res => {
        if (res.status === 'success') {
          setStatus('success');
          setTimeout(() => navigate('/profile'), 3000);
        } else {
          setStatus('failed');
          setMessage(res.message || 'Payment failed');
        }
      })
      .catch(err => {
        setStatus('failed');
        setMessage(err.message || 'Verification failed');
      });
  }, [navigate]);

  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
        {status === 'processing' && (
          <>
            <MIcon name="hourglass_empty" size="3rem" style={{ animation: 'spin 1s linear infinite' }} />
            <h2>{lang === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}</h2>
            <p>{lang === 'fr' ? 'Veuillez patienter...' : 'Please wait...'}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <MIcon name="check_circle" size="4rem" style={{ color: '#4CAF50' }} />
            <h2>{lang === 'fr' ? 'Paiement Réussi !' : 'Payment Successful!'}</h2>
            <p>{lang === 'fr' ? 'Redirection vers votre profil...' : 'Redirecting to your profile...'}</p>
          </>
        )}
        {status === 'failed' && (
          <>
            <MIcon name="cancel" size="4rem" style={{ color: '#f44336' }} />
            <h2>{lang === 'fr' ? 'Paiement Échoué' : 'Payment Failed'}</h2>
            <p>{message}</p>
            <button onClick={() => navigate('/buy-tickets')} className="hp-btn hp-btn-primary" style={{ marginTop: '20px' }}>
              {lang === 'fr' ? 'Réessayer' : 'Try Again'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// PAGE: PAYMENT FAIL
// ============================================
function PaymentFailPage({ navigate, lang }) {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
        <MIcon name="cancel" size="4rem" style={{ color: '#f44336' }} />
        <h2>{lang === 'fr' ? 'Paiement Annulé/Échoué' : 'Payment Cancelled/Failed'}</h2>
        <p>{lang === 'fr' ? 'Votre transaction n\'a pas pu être finalisée.' : 'Your transaction could not be completed.'}</p>
        <button onClick={() => navigate('/buy-tickets')} className="hp-btn hp-btn-primary" style={{ marginTop: '20px' }}>
          {lang === 'fr' ? 'Réessayer' : 'Try Again'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// FALLBACK PAGE
// ============================================
function GenericPage({ title, navigate }) {
  return (
    <>
      <div className="page-header">
        <h1>{title}</h1>
        <div className="breadcrumb">
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a>
          <span>/</span>
          <span>{title}</span>
        </div>
      </div>
      <section className="section">
        <div className="container" style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ marginBottom: '16px' }}><MIcon name="construction" size="4rem" /></div>
          <h2>Coming Soon</h2>
          <p style={{ color: 'var(--hp-gray-500)', margin: '16px auto', maxWidth: '500px' }}>
            This page is under construction. Please check back later!
          </p>
          <a href="/" className="btn-primary" onClick={e => { e.preventDefault(); navigate('/'); }}>
            Back to Home
          </a>
        </div>
      </section>
    </>
  );
}

// ============================================
// MAIN APP
// ============================================
function App() {
  const { path, navigate } = useRouter();
  const [lang, setLang] = useState('en');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.getSettings()
      .then(res => {
        setSettings(res.settings || null);
      })
      .catch(err => {
        console.error("Failed to load settings:", err);
      });
  }, []);

  const renderPage = () => {
    // Dynamic category route: /category/:slug
    if (path.startsWith('/category/')) {
      const slug = path.replace('/category/', '');
      return <CategoryPage slug={slug} navigate={navigate} lang={lang} />;
    }

    // Dynamic attraction route: /attraction/:slug
    if (path.startsWith('/attraction/')) {
      const slug = path.replace('/attraction/', '');
      return <AttractionDetailPage slug={slug} navigate={navigate} lang={lang} />;
    }

    // Dynamic news route: /news/:slug
    if (path.startsWith('/news/')) {
      const slug = path.replace('/news/', '');
      return <NewsArticlePage slug={slug} navigate={navigate} lang={lang} />;
    }

    switch (path) {
      case '/': return <HomePage navigate={navigate} lang={lang} settings={settings} />;
      case '/plan-your-visit': return <PlanVisitPage navigate={navigate} lang={lang} settings={settings} />;
      case '/park-rules': return <ParkRulesPage navigate={navigate} lang={lang} />;
      case '/gallery': case '/our-gallery': return <GalleryPage navigate={navigate} lang={lang} />;
      case '/attractions': return <AttractionsPage navigate={navigate} lang={lang} />;
      case '/events': return <EventsPage navigate={navigate} lang={lang} />;
      case '/news': return <NewsPage navigate={navigate} lang={lang} />;
      case '/contact': return <ContactPage navigate={navigate} lang={lang} />;
      case '/buy-tickets': return <BuyTicketsPage navigate={navigate} lang={lang} />;
      case '/checkout': return <CheckoutPage navigate={navigate} lang={lang} />;
      case '/payment/callback': return <PaymentCallbackPage navigate={navigate} lang={lang} />;
      case '/payment/fail': return <PaymentFailPage navigate={navigate} lang={lang} />;
      case '/login': return <LoginPage navigate={navigate} lang={lang} />;
      case '/register': return <RegisterPage navigate={navigate} lang={lang} />;
      case '/profile': case '/my-profile': return <ProfilePage navigate={navigate} lang={lang} />;
      case '/check-balance': return <CheckBalancePage navigate={navigate} lang={lang} />;
      case '/pro-business': return <ProBusinessPage navigate={navigate} lang={lang} />;
      default: return <GenericPage title={lang === 'fr' ? 'Page non trouvée' : 'Page Not Found'} navigate={navigate} />;
    }
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <Navbar currentPath={path} navigate={navigate} lang={lang} setLang={setLang} />
      <main>{renderPage()}</main>
      <NewsletterSection lang={lang} />
      <Footer navigate={navigate} settings={settings} lang={lang} />
      <ScrollToTop />
    </>
  );
}

export default App;
